import { powerRadarSectionLabel } from "./result-sections";
import {
  Fragment,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type ReactNode,
} from "react";
import { FormattedMessage, useIntl, type IntlShape } from "react-intl";
import { Link, useSearchParams } from "react-router";
import { createPortal } from "react-dom";
import { ELEMENT_CODES, elementDisplayName, elementNameId } from "@/lib/elements";
import { LEVEL_COUNT, SKILL_LEVELS } from "@/lib/skill-levels";
import { mixHex, pickInk } from "@/lib/color";
import { skillLevelBandBackground, skillLevelSwatch } from "@/lib/skill-level-style";
import { useThemePrefs } from "@/stores/theme-prefs";
import { type Msg, tx } from "@/i18n/Msg";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { InfoTooltip } from "@/components/design/InfoTooltip";
import { Tooltip } from "@/components/common/Tooltip";
import { Avatar } from "@/components/common/Avatar";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { LockClosedIcon } from "./lock-icons";
import {
  parseTeamResult,
  type InfeasibilityView,
  type RelaxationView,
  type TeamResultView,
} from "./match-result-codec";
import { Disclosure } from "@/components/design/Disclosure";
import { Collapse } from "@/components/design/Collapse";
import { powerGroupLabelId } from "./match-design-rules";
import {
  buildDemographics,
  buildPowerTilts,
  type PowerTilt,
  type TiltAxis,
  type TiltGrade,
  type TiltSide,
  buildElementRequirementRows,
  buildFactSkillFulfillment,
  buildRequirementAnalysis,
  CLUSTER_CARRIER_MIN,
  POWER_STRONG_THRESHOLD,
  buildHeatmapRows,
  buildMemberDetail,
  buildPowerClusters,
  buildPowersCoverage,
  MEAN_MIN_MEMBERS,
  buildPowerColumnGroups,
  buildWishFit,
  buildBoosterCoverage,
  buildClusterCarriers,
  buildSkillCoverage,
  type SkillCoverage,
  leniencyDependentCriteria,
  SKILL_STRONG_LEVEL,
  buildClusterWishes,
  buildElementStats,
  buildPowerRows,
  sortPowerRowsByWish,
  powerWishesUsable,
  elementWish,
  clusterMean,
  compositionNote,
  RADAR_ORIGIN,
  wishTarget,
  CONDITION_LABEL_UNKNOWN,
  constraintSummary,
  memberFactSkillScore,
  labelForCondition,
  readComposition,
  type ConditionLabelFormatters,
  type RequirementAnalysis,
  type RequirementLeniencyView,
  type ResultPerson,
  isLenientLevelMatch,
} from "./match-result-derive";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { RequirementMatrixDiagram } from "./requirement-matrix/RequirementMatrixDiagram";
import type { RequirementMatrixDiagramProps } from "./requirement-matrix/types";
import { CandidateRankingPanelContent } from "./CandidateRankingPanel";
import { useConditionLabelFormatters, useConditionLabelLookup } from "./result-panel-labels";
import { Donut } from "./result-panel-visuals";

export interface ResultTeamLike {
  teamId: string;
  result?: unknown;
  persons: ResultPerson[];
}

export function useTeamResultView(team: ResultTeamLike): TeamResultView {
  return useMemo(() => parseTeamResult(team.result), [team.result]);
}

// ─── Dashboard pieces ─────────────────────────────────────────────────────

// Bei 60 Personen ist das Roster die ganze erste Bildschirmhoehe und das Urteil darunter
// unsichtbar. 24 fuellt jedes Raster glatt (3/8 Spalten) und bleibt eine ueberschaubare Wand.
const TEAM_TILE_CAP = 24;

// Der Deckel-Schalter, den Roster und Anforderungs-Spalten teilen: gleiche Optik, gleiche
// zwei Texte, gleiche aria - vorher zweimal Zeichen fuer Zeichen dasselbe Markup, das beim
// naechsten Anfassen auseinanderlaufen wollte (UI_CONVENTIONS: "Reuse First").
function ShowAllToggle({
  open,
  hidden,
  onToggle,
  className = "mt-3",
  testId,
}: {
  open: boolean;
  // Wie viele Eintraege der Deckel gerade verbirgt - steht im Text des zugeklappten Zustands.
  hidden: number;
  onToggle: () => void;
  className?: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      className={`${className} rounded text-smycles-label tabular-nums text-ink-muted underline decoration-ink/15 underline-offset-2 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui`}
      aria-expanded={open}
      data-testid={testId}
      onClick={onToggle}
    >
      {open ? (
        <FormattedMessage id="match.result.memberCriteriaLess" defaultMessage="weniger anzeigen" />
      ) : (
        <FormattedMessage
          id="match.result.analysis.moreRows"
          defaultMessage="+{count} weitere"
          values={{ count: hidden }}
        />
      )}
    </button>
  );
}

export function PersonAvatarsRow({
  persons,
  view,
  elementWeights,
  requirementLeniency,
  assessOnly = false,
}: {
  persons: ResultPerson[];
  // Ohne view bleibt die Reihe wie zuvor: Avatar = Link aufs Profil. Mit view klappt der
  // Klick stattdessen das Match-Detail der Person auf (spark #236) - das Profil bleibt
  // von dort aus erreichbar.
  view?: TeamResultView;
  elementWeights?: Readonly<Record<string, number>>;
  // Kulanz-Regel des Results: entscheidet, ob ein KI-Treffer "erfuellt" oder "kulant erfuellt" ist.
  requirementLeniency?: RequirementLeniencyView | null;
  assessOnly?: boolean;
}) {
  const intl = useIntl();
  const [openId, setOpenId] = useState<string | null>(null);
  // Ids, deren Detail-Inhalt noch im DOM steht: die offene Person, plus jede Person, die
  // gerade zuklappt (ihre Collapse-Zeile ist schon `open=false`, aber die Schliess-Animation
  // laeuft noch). Ohne dieses zweite Set wuerde ein Wechsel A -> B nur an einer Stelle einen
  // Sprung zeigen statt an beiden Positionen gleichzeitig smooth zu- und aufzuklappen.
  const [mountedIds, setMountedIds] = useState<Set<string>>(new Set());
  const toggle = (id: string) => {
    setOpenId((prev) => {
      const next = prev === id ? null : id;
      if (next !== null) setMountedIds((m) => (m.has(next) ? m : new Set(m).add(next)));
      return next;
    });
  };
  // Spaltenzahl aus dem tatsaechlich gerenderten Grid gelesen, nicht als zweite Kopie der
  // @sm:/@lg:-Breakpoints in Pixelwerten nachgebaut - bleibt automatisch synchron, falls sich
  // die Tailwind-Klassen am Grid mal aendern. jsdom liefert keine echte Grid-Berechnung
  // (leerer String), daher der Fallback auf 3 (kleinste Stufe) in Tests.
  const [showAll, setShowAll] = useState(false);
  const gridRef = useRef<HTMLDivElement>(null);
  const [columns, setColumns] = useState(3);
  useLayoutEffect(() => {
    const el = gridRef.current;
    if (!el) return;
    const measure = () => {
      const count = getComputedStyle(el).gridTemplateColumns.split(" ").filter(Boolean).length;
      if (count > 0) setColumns((prev) => (count === prev ? prev : count));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  if (persons.length === 0) {
    return (
      <div className="text-smycles-meta text-ink-muted">
        <FormattedMessage
          id="match.result.solverNotRun"
          defaultMessage="Solver hasn't run yet."
        />
      </div>
    );
  }
  const open = view ? persons.find((p) => p.id === openId) ?? null : null;

  function renderTile(p: ResultPerson, firstRow: boolean) {
    const isOpenMember = view != null && openId === p.id;
    const dimmed = open != null && !isOpenMember;
    const body = (
      <>
        <div className="mx-auto flex justify-center">
          <Avatar
            person={{ name: p.name || "?", avatarContent: p.avatarContent, avatarMime: p.avatarMime }}
            size="lg"
          />
        </div>
        <div
          className={`mt-2 truncate text-smycles-meta font-medium transition ${
            dimmed ? "text-ink-muted group-hover:text-ink" : "text-ink group-hover:text-ink-accent"
          }`}
        >
          {p.name}
        </div>
        {p.profession ? (
          <div
            className={`truncate text-smycles-label transition ${dimmed ? "text-ink-muted" : "text-ink-muted"}`}
            title={p.profession}
          >
            {p.profession}
          </div>
        ) : null}
      </>
    );
    // Row-Abstand kommt bewusst von der Kachel selbst, nicht vom Grid-row-gap: der Detail-
    // Zeilen-Wrapper direkt davor steht in JEDER Reihe im DOM, auch geschlossen mit 0 Hoehe
    // (siehe renderDetailRow) - ein Grid-row-gap wuerde dann VOR UND NACH diesem leeren Track
    // greifen und die Personen-Reihen doppelt so weit auseinanderziehen wie vorher.
    const outerShell = `min-w-0 text-center ${firstRow ? "" : "mt-4"}`;
    const tileShell =
      "group w-full rounded-2xl px-3 py-2 text-center outline-none transition focus-visible:ring-2 focus-visible:ring-ink-accent-ui";
    if (!view) {
      return (
        <Link
          key={p.id}
          to={`/p/${p.id}`}
          className={`${outerShell} block ${tileShell}`}
          title={intl.formatMessage(
            { id: "match.result.openProfileTitle", defaultMessage: "{name} – open profile" },
            { name: p.name },
          )}
        >
          {body}
        </Link>
      );
    }
    const isOpen = openId === p.id;
    const detailId = `member-detail-${p.id}`;
    return (
      <div key={p.id} className={outerShell}>
        <button
          type="button"
          aria-expanded={isOpen}
          aria-controls={isOpen ? detailId : undefined}
          id={`result-team-member-${p.id}`}
          onClick={() => toggle(p.id)}
          className={`${tileShell} cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui ${
            isOpen ? "bg-ink/10" : "hover:bg-ink/[0.06]"
          }`}
        >
          {body}
        </button>
        {/* Nur an der aufgeklappten Kachel, nicht permanent an allen - sonst kaempft der
            Link staendig um Aufmerksamkeit mit 25 anderen, die niemand gerade anschaut.
            Eigener Link statt Teil des Toggle-Buttons: ein <a> in einem <button> ist
            ungueltiges HTML. "Verlaesst die Seite" -> ↗ (UI_CONVENTIONS.md, Pfeil-Glyphen),
            nicht › (das waere fuer das Auf-/Zuklappen selbst reserviert, das der Button
            uebernimmt). */}
        {isOpen ? (
          <Link
            to={`/p/${p.id}`}
            className="mt-1 inline-flex items-center gap-1 text-smycles-micro uppercase tracking-[0.2em] text-ink-muted outline-none transition hover:text-ink-accent focus-visible:ring-2 focus-visible:ring-ink-accent-ui rounded"
          >
            <FormattedMessage id="match.result.memberOpenProfile" defaultMessage="Profil" />{" "}
            <span aria-hidden="true">↗</span>
          </Link>
        ) : null}
      </div>
    );
  }

  // Steht am Ende der Reihe, in der die Kachel dieser Person liegt - nicht direkt hinter ihr.
  // Sonst bleibt der Rest dieser Reihe als Luecke stehen, weil eine volle Spaltenbreite mitten
  // in einer unvollstaendigen Reihe automatisch in die naechste Zeile umbricht (spark #236).
  function renderDetailRow(p: ResultPerson) {
    const isOpen = openId === p.id;
    const detailId = `member-detail-${p.id}`;
    return (
      <div key={`detail-${p.id}`} id={`result-team-detail-${p.id}`} className="col-span-full" data-testid={`team-member-detail-row-${p.id}`}>
        <Collapse
          open={isOpen}
          onClosed={() =>
            setMountedIds((m) => {
              if (!m.has(p.id)) return m;
              const next = new Set(m);
              next.delete(p.id);
              return next;
            })
          }
        >
          {mountedIds.has(p.id) ? (
            <div className="pl-3">
              <MemberDetailPanel
                id={detailId}
                person={p}
                view={view as TeamResultView}
                elementWeights={elementWeights}
                requirementLeniency={requirementLeniency}
                assessOnly={assessOnly}
              />
            </div>
          ) : null}
        </Collapse>
      </div>
    );
  }

  const visible = showAll ? persons : persons.slice(0, TEAM_TILE_CAP);
  const rows: ResultPerson[][] = [];
  for (let i = 0; i < visible.length; i += columns) {
    rows.push(visible.slice(i, i + columns));
  }

  return (
    <div>
      <div
        ref={gridRef}
        data-testid="team-grid"
        className="grid grid-cols-3 gap-x-4 @sm:grid-cols-5 @lg:grid-cols-8"
      >
        {rows.map((row, rowIndex) => (
          <Fragment key={rowIndex}>
            {row.map((p) => renderTile(p, rowIndex === 0))}
            {/* Wrapper steht fuer JEDE Person, nicht nur die gerade gemounteten - sonst wird
                er beim allerersten Oeffnen im selben Render gemountet UND sofort auf offen
                gesetzt, es gibt keinen vorherigen "zu"-Frame, von dem aus die grid-rows-
                Transition ueberhaupt starten koennte. Der teure Inhalt (MemberDetailPanel)
                bleibt trotzdem nur fuer mountedIds gemountet. */}
            {view ? row.map(renderDetailRow) : null}
          </Fragment>
        ))}
      </div>
      {persons.length > TEAM_TILE_CAP && (
        <ShowAllToggle
          open={showAll}
          hidden={persons.length - TEAM_TILE_CAP}
          className="mt-4"
          testId="team-grid-show-all"
          onToggle={() => {
            // Beim Einklappen muss ein Detail mitgehen, dessen Kachel gerade verschwindet -
            // sonst bleibt es unsichtbar offen und die Demografie unten fehlt weiter.
            if (showAll && persons.findIndex((p) => p.id === openId) >= TEAM_TILE_CAP) setOpenId(null);
            setShowAll(!showAll);
          }}
        />
      )}
      {/* Die Demografie beschreibt das Team, nicht die offene Person - unter einem offenen
          Detail wuerde sie als deren Alter gelesen (spark #236). */}
      {view && !open ? <DemographicsRow view={view} /> : null}
    </div>
  );
}

// Verdikt einer Neigung als Text - geteilt vom Kopf der Match-Analyse und dem Mitglied-Detail.
function tiltVerdict(tilt: PowerTilt, intl: IntlShape): string {
  const direction =
    tilt.side === null || tilt.grade === null
      ? null
      : intl.formatMessage(
          { id: `match.result.tilt.grade.${tilt.grade}` },
          { side: intl.formatMessage({ id: `match.result.tilt.side.${tilt.side}` }) },
        );
  // Substanz und Richtung sind unabhaengig und koennen gleichzeitig gelten: 45% gegen 0% starke
  // Powers ist beidseitig schwach UND deutlich geneigt. Vorher schluckte "beidseitig schwach"
  // die Richtung - die Zahl dafuer war berechnet und wurde weggeworfen (spark #236).
  if (tilt.bothWeak) {
    const weak = intl.formatMessage({
      id: "match.result.tilt.bothWeak",
      defaultMessage: "beidseitig schwach",
    });
    return direction === null ? weak : `${weak} · ${direction}`;
  }
  return direction ?? intl.formatMessage({ id: "match.result.tilt.balanced", defaultMessage: "ausgeglichen" });
}

// Ab diesem Abstand zum Team ist "hebt das Team hier" eine Aussage und kein Rundungsrest.

// Ausschlag, bei dem der Abweichungsbalken die halbe Spur fuellt. 12 Punkte, weil
// Cluster-Abstaende zum Teammittel real bis ~15 gehen; groesser wuerde alles flach aussehen.
const MEMBER_DELTA_FULL_SCALE = 12;

function formatDelta(delta: number): string {
  const rounded = Math.round(delta);
  if (rounded === 0) return "±0";
  return rounded > 0 ? `+${rounded}` : `−${Math.abs(rounded)}`;
}

// Balken auf der Mitte: die Mitte IST das Team-Niveau, links darunter, rechts darueber.
function DeltaBar({ delta, redundant = false }: { delta: number; redundant?: boolean }) {
  const pct = Math.min(50, (Math.abs(delta) / MEMBER_DELTA_FULL_SCALE) * 50);
  return (
    <span className="flex items-center gap-1">
      <span className="relative block h-[3px] min-w-0 flex-1 rounded bg-ink/10">
      <span className="absolute inset-y-[-2px] left-1/2 w-px bg-ink/40" />
      <span
        className="absolute top-0 block h-[3px] rounded"
        style={{
          width: `${pct}%`,
          [delta >= 0 ? "left" : "right"]: "50%",
          background: "var(--color-accent)",
        }}
      />
      </span>
      {!redundant ? <span className="text-smycles-micro tabular-nums text-ink">{formatDelta(delta)}</span> : null}
    </span>
  );
}

// Aufgeklapptes Match-Detail einer Person. Zwei Spalten, jede Aussage genau einmal:
// links die Anforderungen dieser Person mit Nachweis (Identitaet), rechts ihr Powers-Profil
// gegen das eigene Team (vergleichend). Bewusst KEINE Zusammenfassungs-Zeile daneben - eine
// gezaehlte Zusammenfassung neben der Liste, die sie zaehlt, ist dieselbe Aussage zweimal.
// Der Zaehler steht deshalb als Annotation am Spaltenkopf, nicht als eigener Satz.
// So viele Anforderungen stehen offen da; der Rest kommt per Umschalter. Fuenf, weil die
// Spalte damit etwa so hoch wird wie die sechs Cluster-Zeilen daneben.
const MEMBER_CRITERIA_CAP = 5;

function MemberDetailPanel({
  id,
  person,
  view,
  elementWeights,
  requirementLeniency,
  assessOnly = false,
}: {
  id: string;
  person: ResultPerson;
  view: TeamResultView;
  elementWeights?: Readonly<Record<string, number>>;
  requirementLeniency?: RequirementLeniencyView | null;
  // Bewerten: das Team war vorgegeben, der Solver hat es nicht ausgewaehlt. "Warum im Team"
  // waere dann eine Behauptung ueber eine Entscheidung, die niemand getroffen hat - dieselbe
  // Fehldeutung, gegen die fitNote schon abgesichert ist (spark #236).
  assessOnly?: boolean;
}) {
  const intl = useIntl();
  const labelLookup = useConditionLabelLookup();
  const labelFormatters = useConditionLabelFormatters(intl);
  const detail = useMemo(
    () => buildMemberDetail(view.factSkill, view.powers, person.id, elementWeights),
    [view.factSkill, view.powers, person.id, elementWeights],
  );
  // Aufgeklappte KI-Begründungen, je Kriterium-Index dieser Person.
  const [openReasons, setOpenReasons] = useState<Set<number>>(new Set());
  const [allCriteria, setAllCriteria] = useState(false);
  const toggleReason = (index: number) =>
    setOpenReasons((prev) => {
      const next = new Set(prev);
      if (!next.delete(index)) next.add(index);
      return next;
    });
  if (!detail) {
    return (
      <div id={id} className="mt-5">
        <EmptyHint />
      </div>
    );
  }
  const { requirements: req } = detail;
  // Gegen das eigene Team gelesen: der Abstand zum Team-Cluster-Mittel, nicht der Absolutwert.
  // Absolut ist das Profil (ein Klick weiter), hier zaehlt "wo hebt/senkt die Person das Team".
  // Ohne eine einzige eingeschaetzte Power sind Powers-Spalte UND Charakter-Spalte leer -
  // beide leben ausschliesslich von Power-Werten. Zwei blanke, beschriftete Spalten lesen sich
  // wie ein Ladefehler; die Person ist stattdessen der Ort, an dem die Luecke benannt gehoert
  // (spark #236, @adr SPARK-0037-unmeasured-powers-are-absent-not-zero@2).
  const hasPowers = detail.clusters.some((c) => c.assessed > 0);
  const relative = detail.clusters
    .filter((c) => c.score != null && c.teamScore != null)
    .map((c) => ({
      key: c.key,
      score: c.score as number,
      delta: (c.score as number) - (c.teamScore as number),
      strong: c.strong,
      assessed: c.assessed,
    }))
    .sort((a, b) => b.delta - a.delta);
  // Was die Person BEITRAEGT steht oben - danach wird gelesen ("keep or swap"). Erst
  // erfuellt (kritische zuerst, die zerbrechen ohne sie), dann teilweise, dann offen.
  // Innerhalb gleicher Erfuellung Muss vor Kann, weil ein offenes Muss die haertere Aussage
  // ist. Der Rest liegt hinter einem Umschalter statt die Spalte zu fluten.
  const sortedCriteria = detail.criteria
    .map((c, i) => ({ c, i }))
    .sort((x, y) => {
      const fx = x.c.fulfillment ?? 0;
      const fy = y.c.fulfillment ?? 0;
      if ((fy >= 1 ? 1 : 0) !== (fx >= 1 ? 1 : 0)) return (fy >= 1 ? 1 : 0) - (fx >= 1 ? 1 : 0);
      if (x.c.critical !== y.c.critical) return Number(y.c.critical) - Number(x.c.critical);
      if (fy !== fx) return fy - fx;
      return Number(y.c.requirement === "MUST") - Number(x.c.requirement === "MUST");
    });
  const openMusts = sortedCriteria.filter(
    ({ c }) => c.requirement === "MUST" && (c.fulfillment ?? 0) < 1,
  );
  const shownCriteria = allCriteria
    ? sortedCriteria
    : (() => {
        const head = sortedCriteria.slice(0, MEMBER_CRITERIA_CAP);
        const missing = openMusts.filter((m) => !head.includes(m));
        return missing.length > 0 ? [...head, ...missing] : head;
      })();
  const label = (c: unknown) => labelForCondition(c, labelLookup, labelFormatters);
  // Muss zuerst und mit eigener Formulierung: das ist der Satz, der eine Entscheidung
  // erzwingt. Kann-Kriterien, die auch an dieser Person haengen, kommen danach.
  const criticalMust = detail.criticalMustFor.map(label);
  const criticalOptional = detail.criticalFor
    .filter((c) => !detail.criticalMustFor.includes(c))
    .map(label);
  // Kopfzeile: eine Zeile Prosa ueber der Detailebene, nicht neben ihr. Sie sagt, was an
  // DIESER Person besonders ist - Deckung, was ohne sie zerbricht, wo sie das Team hebt.
  // Die Spalten darunter sind die Aufloesung dazu; deshalb traegt der Spaltenkopf keine
  // zweite Zaehlung mehr (sonst steht dieselbe Aussage zweimal auf gleicher Ebene).
  // Einzigartige Kompetenzen: bis zu drei benannt, der Rest gezaehlt. Ein reiner Zaehler
  // ("bringt 4 Kompetenzen allein") laesst genau die Frage offen, die zaehlt - WELCHE.
  // Nur gefragte Kompetenzen in der Zusammenfassung: der Satz soll die Entscheidung stuetzen,
  // nicht Zufallswissen aufzaehlen, das nie verlangt war.
  const exclusiveNames = detail.exclusiveRequestedSkills.slice(0, 3).join(", ");
  const exclusiveRest = detail.exclusiveRequestedSkills.length - 3;
  // Nur Befunde, keine Fuellsaetze. Deckung und Muss stehen in der Anforderungs-Spalte
  // 40px darunter, "keine Anforderung haengt allein an dieser Person" und "Powers auf
  // Teamniveau" sagen ausdruecklich, dass es nichts zu sagen gibt - beides war Prosa, die
  // entsteht, weil ein Slot gefuellt werden muss. Ist an einer Person nichts besonders,
  // bleibt die Zeile weg und das Panel beginnt mit den Spalten.
  // Ein Befund = ein Satz. Damit gibt es keine Trennzeichen, die in eine neue Zeile rutschen.
  const findings = [
    criticalMust.length > 0
      ? intl.formatMessage(
          {
            id: "match.result.memberSummaryCriticalMust",
            defaultMessage:
              "Ohne diese Person {count, plural, one {fällt das Muss} other {fallen die Muss-Kriterien}} {list}.",
          },
          { list: criticalMust.join(", "), count: criticalMust.length },
        )
      : null,
    criticalOptional.length > 0
      ? intl.formatMessage(
          { id: "match.result.memberSummaryCritical", defaultMessage: "Kritisch für {list}." },
          { list: criticalOptional.join(", ") },
        )
      : null,
    detail.exclusiveRequestedSkills.length > 0
      ? intl.formatMessage(
          {
            id: "match.result.memberSummaryExclusiveSkills",
            defaultMessage:
              "Einzige mit {list}{rest, plural, =0 {} other { und # weiteren}} auf starker Stufe.",
          },
          { list: exclusiveNames, rest: Math.max(0, exclusiveRest) },
        )
      : null,
    detail.topCoverage
      ? intl.formatMessage({
          id: "match.result.memberSummaryTopCoverage",
          defaultMessage: "Deckt die meisten Anforderungen im Team.",
        })
      : detail.noCoverage
        ? // Ohne Power-Einschätzung ist "der Beitrag liegt in den Powers" keine Einordnung,
          // sondern eine Behauptung über Daten, die es nicht gibt. Dann bleibt nur der Befund
          // selbst - und der ist hier die Aussage: über diese Person ist nichts bekannt, was
          // ihren Platz im Team stützt (spark #236).
          hasPowers
          ? intl.formatMessage({
              id: "match.result.memberSummaryNoCoverage",
              defaultMessage:
                "Erfüllt keine Anforderung – der Beitrag liegt in den Powers, nicht in den Fachkriterien.",
            })
          : intl.formatMessage({
              id: "match.result.memberSummaryNoCoverageNoPowers",
              defaultMessage:
                "Erfüllt keine Anforderung – und ohne Power-Einschätzung ist über den Beitrag sonst nichts bekannt.",
            })
        : null,
    detail.topPowerFit
      ? intl.formatMessage({
          id: "match.result.memberSummaryTopPowerFit",
          defaultMessage: "Höchster Power-Fit im Team.",
        })
      : null,
  ].filter((part): part is string => part !== null);

  const glyphLabel = (met: boolean, partial: boolean, assessable: boolean) =>
    met
      ? intl.formatMessage({ id: "match.result.memberCriterionMet", defaultMessage: "erfüllt" })
      : partial
        ? intl.formatMessage({ id: "match.result.memberCriterionPartial", defaultMessage: "teilweise erfüllt" })
        : assessable
          ? intl.formatMessage({ id: "match.result.memberCriterionOpen", defaultMessage: "nicht erfüllt" })
          : intl.formatMessage({ id: "match.result.memberCriterionUnrated", defaultMessage: "nicht bewertbar" });

  return (
    // Keine Trennlinien: oben steht die ausgewaehlte Kachel, zu der dieses Detail gehoert,
    // und unten endet die Box - solange ein Mitglied offen ist, folgt keine Team-Ebene mehr,
    // die eine Linie trennen muesste (spark #236).
    <div id={id} className="mt-4 space-y-5">
      {/* Name und Beruf stehen schon auf der ausgewaehlten Kachel darueber (Wash markiert
          sie). Sichtbar wiederholt waeren sie dieselbe Aussage zweimal - fuer Screenreader
          bleibt die Ueberschrift, weil die Region sonst ohne Betreff ansagt. Der Link zum
          vollen Profil und das Zuklappen sitzen jetzt direkt an der Kachel selbst (PROFIL ↗
          unter jeder Person, Zuklappen per erneutem Kachel-Klick) - hier waeren sie dieselbe
          Aussage zweimal (spark #236). */}
      <h3 className="sr-only">
        {person.profession ? `${person.name} – ${person.profession}` : person.name}
      </h3>

      {findings.length > 0 ? (
        <div className="max-w-[68ch] text-smycles-body leading-relaxed text-ink">{findings.join(" ")}</div>
      ) : null}

      {/* Nur echte Begruendung. Ohne KI-Text steht hier nichts: ein aus den Zahlen
          zusammengesetzter Satz waere nur die Wiederholung der beiden Spalten in Prosa. */}
      {detail.aiAssessment?.fazit || detail.aiAssessment?.positive ? (
        <div className="min-w-0">
          <div className="smycles-field-label">
            {assessOnly ? (
              <FormattedMessage id="match.result.memberWhySuited" defaultMessage="Warum geeignet" />
            ) : (
              <FormattedMessage id="match.result.memberWhy" defaultMessage="Warum im Team" />
            )}
          </div>
          <div className="mt-2 max-w-prose space-y-1.5 text-smycles-meta leading-snug text-ink">
            {detail.aiAssessment.positive ? <div>{detail.aiAssessment.positive}</div> : null}
            {detail.aiAssessment.fazit ? <div className="text-ink">{detail.aiAssessment.fazit}</div> : null}
          </div>
        </div>
      ) : null}

      {/* Drei Spalten: was die Person abdeckt, wo sie das Team hebt, welchen Charakter sie
          einbringt. Die Prosa darueber hat ihre eigene Zeile, weil Leseweite (50-75 Zeichen)
          und eine Drittel-Spalte bei dieser Panelbreite nicht zusammengehen. */}
      <div
        className={`grid gap-x-8 gap-y-5 @md:grid-cols-2 ${
          hasPowers
            ? "@lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1.1fr)_minmax(0,0.75fr)]"
            : "@lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1.85fr)]"
        }`}
      >
        {/* Hard-Skill-Seite mit Nachweis: eine Zahl sagt nicht, WELCHE und womit belegt. */}
        <div className="min-w-0">
          <div className="smycles-field-label">
            <FormattedMessage id="match.result.memberRequirements" defaultMessage="Anforderungen" />
          </div>
          {/* Die Zahl gehoert an die Liste, nicht in einen Zusammenfassungs-Slot darueber.
              Der Team-Median ist der einzige Teil, den die Liste selbst nicht zeigt: "3 von 7"
              allein sagt nicht, ob das die Spitze des Teams ist oder sein Schlusslicht. */}
          <div className="mt-1 text-smycles-label tabular-nums text-ink-muted">
            {req.teamMedianMet != null ? (
              <FormattedMessage
                id="match.result.memberCoverageVsMedian"
                defaultMessage="{met} von {total} · Team-Median {median, number}"
                values={{ met: req.met, total: req.total, median: req.teamMedianMet }}
              />
            ) : (
              <FormattedMessage
                id="match.result.memberCoverage"
                defaultMessage="{met} von {total}"
                values={{ met: req.met, total: req.total }}
              />
            )}
          </div>
          <div className="mt-2 space-y-1">
            {shownCriteria.map(({ c, i }) => {
              const met = c.fulfillment != null && c.fulfillment >= 1;
              const partial = c.fulfillment != null && c.fulfillment > 0 && c.fulfillment < 1;
              // Kulanz-Treffer: gilt als erfuellt, obwohl die geforderte Stufe nicht belegt ist.
              // Dieselbe Regel wie im Ranken-Modus - dort war sie bisher als Einzige sichtbar.
              const lenient = isLenientLevelMatch(
                c.condition,
                c.requirement,
                c.aiReason,
                met,
                requirementLeniency,
              );
              const reasonOpen = openReasons.has(i);
              return (
                <div key={i} className="min-w-0">
                  <div className="grid grid-cols-[0.75rem_minmax(0,1fr)_auto] items-baseline gap-2 text-smycles-meta">
                    <span
                      className={met ? "text-ink-accent" : "text-ink-muted"}
                      aria-label={glyphLabel(met, partial, c.assessable)}
                    >
                      {met ? "✓" : partial ? "~" : c.assessable ? "○" : "?"}
                    </span>
                    <span className="min-w-0">
                      <span className={met ? "text-ink" : "text-ink-muted"}>
                        {labelForCondition(c.condition, labelLookup, labelFormatters)}
                      </span>
                      {c.requirement === "MUST" ? (
                        <span className="ml-1.5 text-smycles-micro uppercase tracking-[0.12em] text-ink-muted">
                          {intl.formatMessage({ id: "match.result.ranking.must", defaultMessage: "Muss" })}
                        </span>
                      ) : null}
                      {c.critical ? (
                        <span className="ml-1.5 text-smycles-micro uppercase tracking-[0.12em] text-ink-accent">
                          <FormattedMessage id="match.result.memberCriticalTag" defaultMessage="kritisch" />
                        </span>
                      ) : null}
                      {lenient ? (
                        <span
                          className="ml-1.5 text-smycles-micro uppercase tracking-[0.12em] text-ink-muted"
                          title={intl.formatMessage({
                            id: "match.result.ranking.lenientMatchHint",
                            defaultMessage:
                              "Erfüllt per kulanter Anforderungsprüfung: Fachwissen vorhanden, geforderte Stufe nicht belegt.",
                          })}
                        >
                          <FormattedMessage id="match.result.memberLenientTag" defaultMessage="kulant" />
                        </span>
                      ) : null}
                      {/* Dieselbe KI-Begründung wie in Pruefen: genesis schreibt sie pro Kriterium,
                          sobald die Condition mit AI ausgewertet wurde – unabhängig vom Match-Modus.
                          Nur RANK hat sie bisher gerendert. */}
                      {c.aiReason ? (
                        <button
                          type="button"
                          aria-expanded={reasonOpen}
                          onClick={() => toggleReason(i)}
                          className="ml-1.5 inline-flex shrink-0 items-center rounded-full bg-accent/10 px-2 py-0.5 text-smycles-micro font-semibold uppercase leading-none tracking-[0.12em] text-ink-accent ring-1 ring-accent-decoration/20 outline-none transition focus-visible:ring-2 focus-visible:ring-ink-ui"
                          title={intl.formatMessage({
                            id: "match.result.ranking.aiChipTitle",
                            defaultMessage: "Reason from the AI evaluation",
                          })}
                        >
                          <FormattedMessage id="match.result.ranking.aiChip" defaultMessage="AI Reason" />
                        </button>
                      ) : null}
                    </span>
                    <span className="text-right text-smycles-label tabular-nums text-ink-muted">
                      {partial ? `${Math.round((c.fulfillment ?? 0) * 100)} %` : ""}
                    </span>
                  </div>
                  {c.aiReason && reasonOpen ? (
                    <div className="mt-1 max-w-prose pl-[1.25rem] text-smycles-label leading-snug text-ink">
                      {c.aiReason}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          {sortedCriteria.length > MEMBER_CRITERIA_CAP ? (
            <button
              type="button"
              onClick={() => setAllCriteria((v) => !v)}
              className="mt-1.5 cursor-pointer text-smycles-label text-ink-muted outline-none transition hover:text-ink-accent focus-visible:ring-2 focus-visible:ring-ink-accent-ui rounded"
            >
              {allCriteria ? (
                <FormattedMessage id="match.result.memberCriteriaLess" defaultMessage="weniger anzeigen" />
              ) : (
                <FormattedMessage
                  id="match.result.memberCriteriaMore"
                  defaultMessage="alle {n} anzeigen"
                  values={{ n: sortedCriteria.length }}
                />
              )}
            </button>
          ) : null}
        </div>

        <div className="min-w-0">
          <div className="flex items-baseline justify-between gap-3">
            <div className="smycles-field-label flex items-center gap-1.5">
              <span>
                <FormattedMessage
                  id="match.result.memberPowersRelative"
                  defaultMessage="Powers im Teamvergleich"
                />
              </span>
              <InfoTooltip
                ariaLabel={intl.formatMessage({
                  id: "match.result.memberClusterCountInfoAria",
                  defaultMessage: "Erklärung zu Zählung und Abstand anzeigen",
                })}
                title={
                  <FormattedMessage
                    id="match.result.memberClusterCountInfoTitle"
                    defaultMessage="Zählung und Abstand"
                  />
                }
                placement="right"
              >
                {/* In Render-Reihenfolge: Zaehlung, Balken, Absolutwert. Die Zeile trug einmal
                    ein signiertes Delta als Zahl - es ist weg (eine Kodierung je Aussage), also
                    darf der Text kein "+/−" mehr erklaeren. Beide Mittel sind Cluster-Mittel
                    (clusterMean): die Balken-Mitte das des Teams, der Wert am Ende das der Person. */}
                <FormattedMessage
                  id="match.result.memberClusterCountInfoBody"
                  defaultMessage="Zählung: wie viele der 5 Cluster-Powers dieser Person über {threshold} liegen – ein Mittel von 68 kann fünfmal 68 sein oder eine starke Power neben vier schwachen. Balken: er steht auf dem Cluster-Mittel des Teams – nach rechts hebt die Person das Team hier, nach links liegt sie darunter. Am Ende ihr eigenes Cluster-Mittel."
                  values={{ threshold: POWER_STRONG_THRESHOLD }}
                />
              </InfoTooltip>
            </div>
          </div>
          {/* Sortiert nach Abstand zum Team, nicht nach Cluster-Reihenfolge: oben, wo die
              Person das Team hebt, unten, wo sie es senkt. Der Balken steht auf der Mitte
              (= Team-Niveau) und laeuft nach rechts/links; MEMBER_DELTA_FULL_SCALE ist der
              Ausschlag, bei dem er die halbe Spur fuellt. */}
          {!hasPowers && (
            <div className="mt-2 text-smycles-meta leading-snug text-ink" data-testid="member-no-powers">
              <FormattedMessage
                id="match.result.memberNoPowers"
                defaultMessage="Keine Power-Einschätzung hinterlegt. Diese Person zählt in keinem Powers-Schnitt des Teams mit – ihr Beitrag oben steht allein auf den Anforderungen."
              />
            </div>
          )}
          <div className="mt-2 grid gap-y-1.5">
            {relative.map((c) => (
              <div key={c.key} className="grid grid-cols-[minmax(0,1fr)_2rem_4rem_2rem] items-center gap-3 text-smycles-meta">
                <span className="min-w-0 truncate" title={intl.formatMessage({ id: powerGroupLabelId(c.key) })}>
                  {intl.formatMessage({ id: powerGroupLabelId(c.key) })}
                </span>
                {/* Kein Delta als Zahl: der Balken steht auf dem Team-Niveau und zeigt denselben
                    Abstand schon. Zwei Kodierungen einer Aussage waren der Grund, warum die
                    Spalte gedraengt wirkte. Zaehlung: 68 kann "5x68" oder "{91,87,51,55,59}" sein. */}
                <span className="text-right text-smycles-label tabular-nums text-ink-muted">
                  {c.assessed > 0 ? `${c.strong}/${c.assessed}` : ""}
                </span>
                <DeltaBar delta={c.delta} />
                <span className="text-right text-smycles-label tabular-nums text-ink-muted">{Math.round(c.score)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Dritte Spalte: der Charakter, den diese Person einbringt. Power-Fit gehoert hierher
            und nicht an den Kopf der Powers-Spalte - er ist eine Aussage ueber die Passung zum
            Wunsch, keine Zeile des Ist-Profils, und stand dort neben einer fremden Erklaerung.
            Ohne Power-Werte hat sie nichts zu sagen: Power-Fit, Neigungen und getragene Cluster
            kommen alle aus denselben Werten. Dann faellt sie weg, statt als leere Ueberschrift
            stehen zu bleiben. */}
        {hasPowers && (
        <div className="min-w-0">
          <div className="smycles-field-label">
            <FormattedMessage id="match.result.memberCharacter" defaultMessage="Charakter" />
          </div>
          <div className="mt-2 space-y-2 text-smycles-meta">
            {detail.powerFit != null ? (
              <div className="flex items-baseline gap-1.5">
                <span className="tabular-nums text-ink">
                  <FormattedMessage
                    id="match.result.memberPowerFit"
                    defaultMessage="Power-Fit {n}"
                    values={{ n: Math.round(detail.powerFit) }}
                  />
                </span>
                <InfoTooltip
                  ariaLabel={intl.formatMessage({
                    id: "match.result.memberPowerFitInfoAria",
                    defaultMessage: "Erklärung zum Power-Fit dieser Person anzeigen",
                  })}
                  placement="left"
                >
                  <FormattedMessage
                    id="match.result.memberPowerFitInfoBody"
                    defaultMessage="Eigene Powers (0–100), gewichtet mit der Powers Scorecard. Hoch heißt: die Person ist stark genau dort, wo Gewicht liegt – unabhängig davon, wie sie im Teamvergleich steht."
                  />
                </InfoTooltip>
              </div>
            ) : null}
            {detail.tilts.map((t) => (
              <div key={t.axis} className="text-ink-muted">
                {intl.formatMessage({ id: `match.result.tilt.axis.${t.axis}` })}:{" "}
                <span className="text-ink">{tiltVerdict(t, intl)}</span>
              </div>
            ))}
            {detail.anchors.length > 0 ? (
              <div className="text-ink-muted">
                <FormattedMessage
                  id="match.result.memberAnchors"
                  defaultMessage="Trägt: {list}"
                  values={{
                    list: detail.anchors
                      .map((k) => {
                        const name = intl.formatMessage({ id: powerGroupLabelId(k) });
                        // "allein" gehoert an das Cluster, nicht in einen eigenen Satz: sonst
                        // steht dieselbe Aussage zweimal auf zwei Ebenen.
                        return detail.soleAnchors.includes(k)
                          ? intl.formatMessage(
                              { id: "match.result.memberAnchorSole", defaultMessage: "{name} (allein)" },
                              { name },
                            )
                          : name;
                      })
                      .join(", "),
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

// Steht in JEDEM Powers-Kasten, dessen Zahlen ueber die gemessene Teilmenge laufen. Der Nenner
// allein reicht nicht: "24/39" liest sich als das ganze Team, solange nirgends steht, dass es 40
// sind. Wiederholung ist hier kein Laerm, sondern die Bedingung dafuer, dass jeder Kasten fuer
// sich richtig gelesen wird - auch einzeln, aus dem Zusammenhang gerissen (spark #236).
// Ohne Luecke rendert sie nichts.
function PowersBasisNote({ view, className = "mb-3" }: { view: TeamResultView; className?: string }) {
  const intl = useIntl();
  const { measured, total } = buildPowersCoverage(view.powers);
  if (total === 0 || measured >= total) return null;
  return (
    <div
      className={`${className} flex items-center gap-1.5 text-smycles-label tabular-nums text-ink-muted`}
      data-testid="powers-basis"
    >
      <FormattedMessage
        id="match.result.powersBasis"
        defaultMessage="Basis: {measured} von {total} Personen"
        values={{ measured, total }}
      />
      <InfoTooltip
        ariaLabel={intl.formatMessage({
          id: "match.result.powersCoverageInfoAria",
          defaultMessage: "Erklärung zur Powers-Abdeckung anzeigen",
        })}
      >
        <FormattedMessage
          id="match.result.powersCoverageInfo"
          defaultMessage="Für {missing, plural, one {# Person} other {# Personen}} im Team liegt keine Power-Einschätzung vor. Alle Powers-Schnitte und -Zählungen laufen nur über die übrigen {measured}; wer fehlt, steht am Ende der Power-Scorecard-Matrix."
          values={{ missing: total - measured, measured }}
        />
      </InfoTooltip>
    </div>
  );
}

export function DemographicsRow({ view }: { view: TeamResultView }) {
  const intl = useIntl();
  const demo = buildDemographics(view.teamMatch);
  const items: Array<{ label: string; value: string }> = [];
  if (demo.dept)
    items.push({
      label: intl.formatMessage({ id: "match.result.dept", defaultMessage: "Dept" }),
      value: demo.dept,
    });
  if (demo.gender)
    items.push({
      label: intl.formatMessage({ id: "match.result.gender", defaultMessage: "Gender" }),
      value: demo.gender,
    });
  if (demo.age)
    items.push({
      label: intl.formatMessage({ id: "match.result.age", defaultMessage: "Age" }),
      value: demo.age,
    });
  if (items.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-x-8 gap-y-2 pt-4 text-smycles-meta" data-testid="team-demographics">
      {items.map((it) => (
        <span key={it.label}>
          <span className="smycles-field-label mr-2">{it.label}</span>
          <span className="font-medium text-ink tabular-nums">{it.value}</span>
        </span>
      ))}
    </div>
  );
}

// Die oeffentliche Fassade bleibt in ResultPanel, waehrend der kandidaten-spezifische Block in
// seinem eigenen Modul lebt. Das Detail bleibt hier, weil es die geteilten Radar-Bausteine nutzt.
export function CandidateRankingPanel(
  props: Omit<ComponentProps<typeof CandidateRankingPanelContent>, "PowerFitDetail">,
) {
  return <CandidateRankingPanelContent {...props} PowerFitDetail={CandidatePowerFitDetail} />;
}

// Eine Achse: beide Pole benannt, die Neigung als Marke auf der Spur. Die Pole MUESSEN
// dastehen - "deutlich Anziehung" allein sagt nicht, wogegen, und Antrieb/Anziehung und
// Exploit/Explore sind kein Allgemeinwissen. Die Marke sitzt auf einer von drei Stufen, also
// genau so grob wie das Wort darunter; eine stufenlose Position waere die Scheinpraezision,
// die buildPowerTilts absichtlich weglaesst (spark #236).
const TILT_OFFSET: Record<TiltGrade, number> = { LEANS: 14, CLEAR: 28, STRONG: 42 };
const TILT_POLES: Record<TiltAxis, [TiltSide, TiltSide]> = {
  DRIVE: ["PUSH", "PULL"],
  MINDSET: ["EXPLOIT", "EXPLORE"],
};

function TiltReading({ tilt }: { tilt: PowerTilt }) {
  const intl = useIntl();
  const [left, right] = TILT_POLES[tilt.axis];
  // Die Marke traegt nur noch die RICHTUNG (Mitte = keine benennbare). Die Substanz stand mal in
  // ihrer Fuellung - hohl bei beidseitig schwach -, aber hohl auf hellem Grund war kaum zu sehen,
  // und "beidseitig schwach" steht ohnehin im Urteilstext darunter. Ein zweiter Kanal, den man
  // suchen muss, um zu lesen, was daneben schon dasteht, ist keiner (spark #236).
  const offset = tilt.grade === null ? null : TILT_OFFSET[tilt.grade];
  const percent = offset === null ? 50 : 50 + (tilt.side === left ? -offset : offset);
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2 text-smycles-label text-ink-muted">
        <span>{intl.formatMessage({ id: `match.result.tilt.side.${left}` })}</span>
        <span>{intl.formatMessage({ id: `match.result.tilt.side.${right}` })}</span>
      </div>
      <div className="relative mt-2 h-[3px] rounded bg-ink/10">
        <span className="absolute inset-y-[-2px] left-1/2 w-px bg-ink/40" />
        <span
          data-testid={`tilt-marker-${tilt.axis}`}
          className="absolute top-1/2 h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ink"
          style={{ left: `${percent}%` }}
        />
      </div>
      <div className="mt-2 text-smycles-meta leading-snug">{tiltVerdict(tilt, intl)}</div>
      {/* "ausgeglichen" heisst hier NICHT "perfekt gleich", sondern "der Unterschied ist kleiner
          als sein eigener Stichprobenfehler". Ohne diese Zeile liest das Wort wie ein Befund
          statt wie Rauschen. */}
      {tilt.grade === null && (
        <div className="mt-0.5 text-smycles-label text-ink-muted">
          <FormattedMessage
            id="match.result.tilt.notSignificant"
            defaultMessage="Unterschied zu klein, um ihn zu deuten"
          />
        </div>
      )}
    </div>
  );
}

export function MatchAnalysisPanel({
  view,
  persons,
}: {
  view: TeamResultView;
  persons: ResultPerson[];
}) {
  const intl = useIntl();
  const labelLookup = useConditionLabelLookup();
  const conditionLabelFormatters = useConditionLabelFormatters(intl);
  const label = (a: RequirementAnalysis) =>
    labelForCondition(a.condition, labelLookup, conditionLabelFormatters);
  const personById = new Map(persons.map((p) => [p.id, p]));
  const nameOf = (id: string) => personById.get(id)?.name ?? id;

  const analysis = useMemo(() => buildRequirementAnalysis(view.factSkill), [view.factSkill]);
  // Die Umkehrung der Anforderungsliste: Kompetenzen, die das Team stark mitbringt, ohne dass sie
  // angefordert waren. Die einzige Aussage auf der Seite, die etwas zeigt, wonach der Nutzer nicht
  // gesucht hat (spark #236) – deshalb muss sie halten: ein einziger starker Träger ist ein
  // Einzelfall, keine Team-Fähigkeit. Erst ab zwei kann das Team die Kompetenz wirklich liefern.
  // ponytail: 2 = "mehr als eine Person" – einziger Regler; hochziehen, falls die Zeile zu leicht
  // Beliebiges einsammelt.
  const hasRisks = teamStructureRisks(view).any;
  const extraSkills = useMemo(
    () => buildSkillCoverage(view.factSkill).filter((c) => !c.requested && c.strong >= 2),
    [view.factSkill],
  );
  const extraCap = 3; // ponytail: drei Highlights, nicht die halbe Inventarliste.
  const coverage = (a: RequirementAnalysis) => (a.teamSize > 0 ? a.met / a.teamSize : 0);
  // Stärken: breiteste zuerst. Lücken: verletztes Muss zuerst (harte Blocker), dann größte Lücke.
  const strengths = analysis.filter((a) => a.fulfilled).sort((x, y) => coverage(y) - coverage(x));
  const gaps = analysis
    .filter((a) => !a.fulfilled)
    .sort((x, y) => Number(y.isMust) - Number(x.isMust) || x.proximity - y.proximity);
  const musts = analysis.filter((a) => a.isMust);
  const kanns = analysis.filter((a) => !a.isMust);
  const fulfilledPercent =
    analysis.length > 0 ? Math.round((strengths.length / analysis.length) * 100) : 0;

  // Kein Empfehlungsbanner mehr: Lücken sind schlechteste-zuerst sortiert (verletztes Muss, dann
  // größte Lücke), also war das Banner Zeile eins der Lücken-Spalte, nochmal vorgelesen. Der
  // einzige Fall, den die Spalten nicht selbst erzählen können, ist "nichts offen" - der steht
  // jetzt als Aussage in der leeren Lücken-Spalte (spark #236).

  // Die Neigung ist kein Befund: sie hat keine gute Richtung und kann darum weder Stärke noch
  // Lücke sein. Sie sagt, WAS FÜR EIN Team das ist - also steht sie als Unterzeile am Kopf und
  // nicht in einer der drei Spalten. Der Schwellwert, der sie definiert, steht im Info-Text; ihn
  // zusätzlich in den Kopf zu schreiben, sägte dasselbe zwei Zoll weiter nochmal.
  const tilts = buildPowerTilts(view.powers);
  const header = (
    <>
      <BoxHeader
        className={tilts.length > 0 ? "mb-2" : undefined}
        title={<FormattedMessage id="match.result.insights.header" defaultMessage="Match-Analyse" />}
      />
      {tilts.length > 0 && (
        <div className="flex items-center gap-4" data-testid="match-analysis-tilts">
          <div className="grid min-w-0 flex-1 gap-x-8 gap-y-3 @sm:grid-cols-2">
            {tilts.map((t) => (
              <TiltReading key={t.axis} tilt={t} />
            ))}
          </div>
          {/* Kein Titel: die beiden Achsen sind daneben beschriftet, eine Ueberschrift daeruber
              waere eine dritte Benennung derselben Sache - und "Team-Charakter" war der Name der
              geloeschten Box, nicht dieser Zeile. */}
          <InfoTooltip
            ariaLabel={intl.formatMessage({
              id: "match.result.tilt.infoAria",
              defaultMessage: "Erklärung zu den Team-Achsen anzeigen",
            })}
            placement="top"
          >
            <>
              {tilts.map((t) => (
                <span key={t.axis} className="mb-1 block">
                  <FormattedMessage
                    id={`match.result.tilt.info.${t.axis}`}
                    values={{
                      a: Math.round(t.shares[0]),
                      b: Math.round(t.shares[1]),
                      threshold: POWER_STRONG_THRESHOLD,
                    }}
                  />
                </span>
              ))}
              <span className="block">
                <FormattedMessage
                  id="match.result.tilt.infoNoise"
                  defaultMessage="Die Abstufung richtet sich danach, wie weit der Unterschied seinen eigenen Stichprobenfehler übersteigt: eher = darüber, klar = das Anderthalbfache, deutlich = das Doppelte. Mehr als diese drei Stufen wird nicht angegeben: bei dieser Teamgröße ist der genaue Unterschied nicht belastbar genug, um als Zahl zu gelten."
                />
              </span>
            </>
          </InfoTooltip>
        </div>
      )}
    </>
  );

  if (analysis.length === 0) {
    return (
      <div id="match-analysis" className="box bg-surface p-6">
        {header}
        <EmptyHint />
      </div>
    );
  }

  return (
    <div id="match-analysis" className="box bg-surface p-6">
      {header}

      <div className="mt-4 grid gap-x-8 gap-y-6 border-t border-ink/5 pt-4 @md:grid-cols-2 @xl:grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-w-0 @md:col-span-2 @xl:col-span-1">
          <div className="text-smycles-micro tracking-[0.2em] text-ink-muted">
            <FormattedMessage id="match.result.analysis.fulfilment" defaultMessage="Erfüllung" />
          </div>
          <div className="mt-2 flex items-center gap-4">
            <Donut
              percent={fulfilledPercent}
              label={intl.formatMessage({ id: "match.result.analysis.donutFulfilled", defaultMessage: "erfüllt" })}
            />
            <div className="space-y-1 text-smycles-meta">
              {[
                { key: "must", rows: musts, label: intl.formatMessage({ id: "match.result.ranking.must", defaultMessage: "Muss" }) },
                { key: "kann", rows: kanns, label: intl.formatMessage({ id: "match.result.ranking.optional", defaultMessage: "Kann" }) },
              ].map(({ key, rows, label: kindLabel }) =>
                rows.length > 0 ? (
                  <div key={key} className="flex items-baseline gap-3">
                    <span className="w-10 text-ink-muted">{kindLabel}</span>
                    <span className="tabular-nums">
                      <FormattedMessage
                        id="match.result.analysis.metOfTotal"
                        defaultMessage="{met}/{total} erfüllt"
                        values={{ met: rows.filter((a) => a.fulfilled).length, total: rows.length }}
                      />
                    </span>
                  </div>
                ) : null,
              )}
            </div>
          </div>
        </div>

        <AnalysisColumn
          heading={<FormattedMessage id="match.result.analysis.strengths" defaultMessage="Stärken" />}
          testId="analysis-rows-strengths"
          rows={strengths}
          label={label}
          nameOf={nameOf}
          intl={intl}
          empty={<FormattedMessage id="match.result.analysis.noStrengths" defaultMessage="Noch nichts erfüllt." />}
        />

        <AnalysisColumn
          heading={<FormattedMessage id="match.result.analysis.gaps" defaultMessage="Lücken" />}
          testId="analysis-rows-gaps"
          rows={gaps}
          label={label}
          nameOf={nameOf}
          intl={intl}
          // Kein Platzhalter, sondern der Befund: wenn die Spalte das Einzige ist, was sie sagt,
          // steht sie in voller Tinte.
          empty={
            <FormattedMessage
              id="match.result.analysis.recAllMet"
              defaultMessage="Alle Anforderungen erfüllt."
            />
          }
        />
      </div>

      {/* Der Fuss der Analyse: was am Team zerbrechlich ist, und was es mitbringt, ohne dass
          jemand danach gefragt hat. Beides sind Ein-Satz-Befunde ueber dasselbe Team, also
          stehen sie zusammen - und nur, wenn es sie gibt. */}
      {(hasRisks || extraSkills.length > 0) && (
        <div className="mt-6 space-y-1 border-t border-ink/5 pt-3">
          <StructureStrip view={view} />
          {extraSkills.length > 0 && (
            <div className="text-smycles-meta leading-snug text-ink">
              <FormattedMessage
                id="match.result.analysis.extraSkills"
                defaultMessage="Stark im Team, nicht angefordert: {skills}"
                values={{
                  // buildSkillCoverage sortiert nach starken Traegern — die ersten drei sind wirklich
                  // die staerksten. Die Zaehlung dazu steht in "Top Skills".
                  skills: extraSkills.slice(0, extraCap).map((c) => c.skill).join(", "),
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Eine Zeile = ein Kriterium, team-weit. Balken ist monochrom gestapelt: met (schwarz) + close
// (ink/25) auf der Spur; der Soll-Strich markiert die Composition-Schwelle (nur MIN). Verletzte
// Muss tragen das Schloss.
// Eine Spalte (Staerken oder Luecken): Zeilen mit Deckel, dahinter der Rest in einem
// scrollenden Kasten. Der Deckel war vorher eine Sackgasse - "+3 weitere" sagte, dass es mehr
// gibt, und bot keinen Weg dorthin. Eigene Komponente statt zweimal inline, weil beide Spalten
// denselben Zustand brauchen (offene Zeile, aufgeklappte Liste) - spark #236.
const ANALYSIS_ROW_CAP = 5;
// ponytail: 20rem ~ sechs Zeilen. Hoch genug, dass Scrollen selten ist, niedrig genug, dass die
// Spalte den Rest der Seite nicht wegschiebt.
const ANALYSIS_LIST_MAX = "max-h-80";

function AnalysisColumn({
  heading,
  testId,
  rows,
  empty,
  label,
  nameOf,
  intl,
}: {
  heading: ReactNode;
  testId: string;
  rows: RequirementAnalysis[];
  empty: ReactNode;
  label: (a: RequirementAnalysis) => string;
  nameOf: (id: string) => string;
  intl: IntlShape;
}) {
  // Eine Zeile offen, wie bei den Mitglieder-Details: sonst wird die Spalte eine Liste von Listen.
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? rows : rows.slice(0, ANALYSIS_ROW_CAP);
  return (
    <div className="min-w-0">
      <div className="text-smycles-micro tracking-[0.2em] text-ink-muted">{heading}</div>
      {rows.length > 0 ? (
        <>
          <div
            id={testId}
            className={`mt-2 space-y-3 ${showAll ? `${ANALYSIS_LIST_MAX} overflow-y-auto pr-2` : ""}`}
          >
            {visible.map((a) => (
              <AnalysisBarRow
                key={a.index}
                a={a}
                label={label(a)}
                intl={intl}
                nameOf={nameOf}
                open={openIndex === a.index}
                onToggle={() => setOpenIndex(openIndex === a.index ? null : a.index)}
              />
            ))}
          </div>
          {rows.length > ANALYSIS_ROW_CAP && (
            <ShowAllToggle
              open={showAll}
              hidden={rows.length - ANALYSIS_ROW_CAP}
              onToggle={() => setShowAll(!showAll)}
            />
          )}
        </>
      ) : (
        <div className="mt-2 text-smycles-meta leading-snug text-ink">{empty}</div>
      )}
    </div>
  );
}

function AnalysisBarRow({
  a,
  label,
  intl,
  nameOf,
  open,
  onToggle,
}: {
  a: RequirementAnalysis;
  label: string;
  intl: IntlShape;
  nameOf: (id: string) => string;
  open: boolean;
  onToggle: () => void;
}) {
  // Eine erfüllte Anforderung, die genau eine Person trägt, ist ein Single Point of Failure:
  // geht sie, fällt die Anforderung. Bei einer Lücke sagt met === 1 nichts.
  const soleName = a.fulfilled && a.met === 1 ? nameOf(a.metPersonIds[0] ?? "") : null;
  const metNames = a.metPersonIds.map(nameOf);
  const closeNames = a.closePersonIds.map(nameOf);
  const seg = (n: number) => (a.teamSize > 0 ? Math.min(100, (n / a.teamSize) * 100) : 0);
  const note = compositionNote(a);
  // Der Soll-Strich haengt an derselben Regel wie der Text: keine Auflage -> kein Strich.
  const targetPct =
    note != null && a.required != null && a.teamSize > 0
      ? Math.min(100, (a.required / a.teamSize) * 100)
      : null;
  // "3 von 8" statt "3 erfüllt": die Zahlen im Balken sind Personen, die Zahl im Label
  // ist eine Stufe/Schwelle. Ohne den Nenner lesen sich beide gleich (spark #236).
  const parts = [
    intl.formatMessage(
      { id: "match.result.analysis.rowMetOf", defaultMessage: "{met} von {total}" },
      { met: a.met, total: a.teamSize },
    ),
  ];
  if (a.close > 0) {
    parts.push(intl.formatMessage({ id: "match.result.analysis.rowClose", defaultMessage: "{n} knapp" }, { n: a.close }));
  }
  const right = note ? intl.formatMessage({ id: note.id }, note.values) : null;
  return (
    <div className="min-w-0">
      <button
        type="button"
        className="w-full min-w-0 rounded text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
        aria-expanded={open}
        aria-label={intl.formatMessage(
          { id: "match.result.analysis.rowToggleAria", defaultMessage: "Träger von {label} anzeigen" },
          { label },
        )}
        onClick={onToggle}
      >
      <div className="smycles-field-value flex items-center gap-1 text-ink">
        {a.isMust && (
          <span className="shrink-0 text-ink-muted" aria-hidden="true">
            <LockClosedIcon />
          </span>
        )}
        <span className="truncate">{label}</span>
      </div>
      <div className="relative mt-2 h-4">
        <div className="absolute inset-x-0 top-1/2 flex h-[3px] -translate-y-1/2 overflow-hidden rounded bg-ink/10">
          <div className="h-full" style={{ width: `${seg(a.met)}%`, background: "var(--color-accent)" }} />
          <div className="h-full bg-ink/40" style={{ width: `${seg(a.close)}%` }} />
        </div>
        {targetPct != null && (
          <div
            className="absolute top-1/2 h-2.5 w-[2px] -translate-y-1/2 rounded bg-ink/40"
            style={{ left: `${targetPct}%` }}
            aria-hidden="true"
          />
        )}
      </div>
      <div className="mt-0.5 flex justify-between gap-3 text-smycles-label tabular-nums text-ink-muted">
        <span className="min-w-0 truncate">
          {parts.join(" · ")}
          {soleName ? (
            <span className="text-ink">
              {" · "}
              <FormattedMessage
                id="match.result.analysis.soleCarrier"
                defaultMessage="nur {name}"
                values={{ name: soleName }}
              />
            </span>
          ) : null}
        </span>
        {right && <span className="shrink-0">{right}</span>}
      </div>
      </button>
      {/* Die Auflösung der Zählung: "0 von 2" sagt nicht, ob niemand in der Nähe ist oder zwei
          knapp daneben liegen. Erst hier wird die Einheit benannt (spark #236). */}
      {open && (
        <div className="mt-2 space-y-0.5 border-l-2 border-ink/10 pl-3 text-smycles-label leading-snug text-ink">
          {metNames.length > 0 && (
            <div>
              <FormattedMessage
                id="match.result.analysis.carriersMet"
                defaultMessage="Erfüllt von: {names}"
                values={{ names: metNames.join(", ") }}
              />
            </div>
          )}
          {closeNames.length > 0 && (
            <div>
              <FormattedMessage
                id="match.result.analysis.carriersClose"
                defaultMessage="Knapp: {names}"
                values={{ names: closeNames.join(", ") }}
              />
            </div>
          )}
          {metNames.length === 0 && closeNames.length === 0 && (
            <div>
              <FormattedMessage
                id="match.result.analysis.carriersNone"
                defaultMessage="Niemand im Team erfüllt das."
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Die Zaehlungen hinter der Zeile, getrennt von ihrem Text: der Fuss der Match-Analyse muss
// wissen, OB es einen Befund gibt, bevor er seine Trennlinie zeichnet - ohne die Zeile dafuer
// zweimal zu formatieren.
function teamStructureRisks(view: TeamResultView) {
  const soleRequirements = buildRequirementAnalysis(view.factSkill).filter(
    (a) => a.fulfilled && a.met === 1,
  ).length;
  const violatedMust = buildFactSkillFulfillment(view.factSkill, view.teamMatch).violatedMustCount;
  // Traegerlose Cluster: nur wo ueberhaupt genug gemessen wurde, um die Aussage zu tragen.
  // Diese Zeile nennt ihren Nenner nicht (sie sagt "6 Cluster", nicht "6 von 40 Personen"),
  // also gilt hier dieselbe Untergrenze wie fuer die Radare (@adr SPARK-0037-unmeasured-powers-are-absent-not-zero@2).
  const emptyClusters = buildClusterCarriers(view.powers).filter(
    (c) => c.carriers === 0 && c.assessed >= MEAN_MIN_MEMBERS,
  ).length;
  return {
    soleRequirements,
    violatedMust,
    emptyClusters,
    any: soleRequirements > 0 || violatedMust > 0 || emptyClusters > 0,
  };
}

export function StructureStrip({ view }: { view: TeamResultView }) {
  const intl = useIntl();
  const { soleRequirements, violatedMust, emptyClusters } = teamStructureRisks(view);
  const risks = [
    soleRequirements > 0
      ? intl.formatMessage(
          {
            id: "match.result.riskSoleRequirement",
            defaultMessage:
              "{count, plural, one {# Anforderung hängt an einer Person} other {# Anforderungen hängen an einer Person}}",
          },
          { count: soleRequirements },
        )
      : null,
    emptyClusters > 0
      ? intl.formatMessage(
          {
            id: "match.result.riskEmptyCluster",
            defaultMessage:
              "{count, plural, one {# Cluster ohne Träger} other {# Cluster ohne Träger}}",
          },
          { count: emptyClusters },
        )
      : null,
    violatedMust > 0
      ? intl.formatMessage(
          {
            id: "match.result.riskViolatedMust",
            defaultMessage: "{count, plural, one {# Muss verletzt} other {# Muss verletzt}}",
          },
          { count: violatedMust },
        )
      : null,
  ].filter((r): r is string => r !== null);
  if (risks.length === 0) return null;
  // Zeile, kein Kasten: die Aussage gehoert in den Fuss der Match-Analyse, neben "Stark im Team,
  // nicht angefordert". Als eigener Kasten darunter stand sie ohne Bezug zwischen der Analyse
  // und den Powers und las sich wie ein neues Thema (spark #236).
  return (
    <div className="text-smycles-meta leading-snug text-ink" data-testid="structure-risks">
      {risks.join(" · ")}
    </div>
  );
}

// Profilstruktur: beantwortet "wie ist dieses Team gebaut", nicht "wie stark ist es".
// Beide Bloecke zaehlen Powers ueber einem Schwellwert statt zu mitteln - bei echten Teams
// liegen die Cluster-Mittel nur 3-7 Punkte auseinander, die Zaehlungen dagegen weit
// (Whitepaper Kap. 6.1.3 Cluster-Bonus, Kap. 7.8 Booster-Luecken-Analyse).
export function ProfileStructurePanel({ view }: { view: TeamResultView }) {
  const intl = useIntl();
  const carriers = buildClusterCarriers(view.powers);
  const boosters = buildBoosterCoverage(view.powers);
  return (
    <div className="box bg-surface p-5">
      <BoxHeader
        title={
          <FormattedMessage
            id="match.result.structureHeader"
            defaultMessage="Powers Profilstruktur"
          />
        }
        right={
          <span className="text-smycles-label text-ink-muted">
            <FormattedMessage
              id="match.result.structureThresholdHint"
              defaultMessage="Powers über {threshold}"
              values={{ threshold: POWER_STRONG_THRESHOLD }}
            />
          </span>
        }
      />
      {carriers.length === 0 && boosters.length === 0 ? (
        <EmptyHint />
      ) : (
        <>
        <PowersBasisNote view={view} />
        {/* Traeger und Luecken - die Aufloesung. Die Neigungs-Achsen standen hier im Kopf und
            stehen jetzt im Kopf der Match-Analyse: sie sind die Aussage UEBER das Team,
            nicht in ihm (spark #236). */}
        <div className="grid gap-x-8 gap-y-6 @md:grid-cols-2">
          {carriers.length > 0 && (
            <div className="min-w-0">
              <div className="smycles-field-label flex items-center gap-1.5">
                <span>
                  <FormattedMessage
                    id="match.result.structureCarriers"
                    defaultMessage="Cluster-Träger"
                  />
                </span>
                <InfoTooltip
                  ariaLabel={intl.formatMessage({
                    id: "match.result.structureCarriersInfoAria",
                    defaultMessage: "Erklärung zur Zählung anzeigen",
                  })}
                  title={
                    <FormattedMessage
                      id="match.result.structureCarriersInfoTitle"
                      defaultMessage="Was gezählt wird"
                    />
                  }
                  placement="right"
                >
                  {/* Was ein "Träger" IST, steht in der Hinweiszeile unter der Liste - sichtbar,
                      weil das die Frage jedes Lesers ist und ein Hover-Tooltip keine Definition
                      tragen kann (der Risiko-Streifen oben benutzt das Wort schon).
                      Offen bleibt der NENNER: buildClusterCarriers zaehlt `assessed`, also nur
                      Personen mit Powers-Daten in diesem Cluster. Bei unvollstaendigen Profilen
                      ist "3 von 5" in einem 7er-Team sonst unerklaerlich - der Datenhinweis
                      oben greift nur, wenn ALLE Powers fehlen, nicht bei Teilbestand. */}
                  <FormattedMessage
                    id="match.result.structureCarriersInfoBody"
                    defaultMessage="Gezählt werden die Personen mit Powers-Daten, nicht das ganze Team: „2 von 6“ heißt zwei Träger unter sechs bewerteten Personen."
                  />
                </InfoTooltip>
              </div>
              <div className="mt-2 space-y-1.5 text-smycles-meta">
                {carriers.map((c) => (
                  <div key={c.key} className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate">
                      {intl.formatMessage({ id: powerGroupLabelId(c.key) })}
                    </span>
                    <span className="shrink-0 tabular-nums text-ink">
                      <FormattedMessage
                        id="match.result.structureOfTeam"
                        defaultMessage="{n} von {total}"
                        values={{ n: c.carriers, total: c.assessed }}
                      />
                      {c.signature > 0 ? (
                        <span className="ml-2 text-ink-muted">
                          <FormattedMessage
                            id="match.result.structureSignature"
                            defaultMessage="· {n} vollständig"
                            values={{ n: c.signature }}
                          />
                        </span>
                      ) : null}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-smycles-label leading-snug text-ink-muted">
                <FormattedMessage
                  id="match.result.structureCarriersHint"
                  defaultMessage="Träger = mindestens {min} von 5 Powers des Clusters über {threshold}; vollständig = alle 5 darüber."
                  values={{ min: CLUSTER_CARRIER_MIN, threshold: POWER_STRONG_THRESHOLD }}
                />
              </div>
            </div>
          )}
          {boosters.length > 0 && (
            <div className="min-w-0">
              <div className="smycles-field-label flex items-center gap-1.5">
                <span>
                  <FormattedMessage
                    id="match.result.structureBoosters"
                    defaultMessage="Booster-Deckung"
                  />
                </span>
                <InfoTooltip
                  ariaLabel={intl.formatMessage({
                    id: "match.result.structureBoostersInfoAria",
                    defaultMessage: "Erklärung zur Booster-Deckung anzeigen",
                  })}
                  title={
                    <FormattedMessage
                      id="match.result.structureBoostersInfoTitle"
                      defaultMessage="Booster und Lücken"
                    />
                  }
                  placement="left"
                >
                  {/* Was ein Booster IST und dass eine duenn gedeckte Zeile die Luecke ist,
                      steht in der Hinweiszeile unter der Liste. Hier bleibt nur, was die Zeile
                      nicht traegt: die Schwelle und was man mit der Luecke macht. */}
                  <FormattedMessage
                    id="match.result.structureBoostersInfoBody"
                    defaultMessage="Gezeigt wird, wie viele der bewerteten Personen die Power stark mitbringen (über {threshold}). Eine solche Lücke lässt sich durch Umbesetzung schließen oder gezielt entwickeln."
                    values={{ threshold: POWER_STRONG_THRESHOLD }}
                  />
                </InfoTooltip>
              </div>
              <div className="mt-2 space-y-1.5 text-smycles-meta">
                {boosters.map((b) => (
                  <div key={b.element} className="grid grid-cols-[minmax(0,1fr)_5rem] items-baseline gap-3">
                    <span className="min-w-0 truncate">
                      <FormattedMessage
                        id={elementNameId(b.element)}
                        defaultMessage={elementDisplayName(b.element)}
                      />
                    </span>
                    <span
                      className={`text-right tabular-nums ${b.covered === 0 ? "text-ink" : "text-ink"}`}
                    >
                      <FormattedMessage
                        id="match.result.structureOfTeam"
                        defaultMessage="{n} von {total}"
                        values={{ n: b.covered, total: b.assessed }}
                      />
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-2 text-smycles-label leading-snug text-ink-muted">
                <FormattedMessage
                  id="match.result.structureBoostersHint"
                  defaultMessage="Booster sind die Powers mit der höchsten Innovationsrelevanz. Eine schwach gedeckte Zeile ist die konkrete Lücke des Teams."
                />
              </div>
            </div>
          )}
        </div>
        </>
      )}
    </div>
  );
}

// Absolute Skill-Struktur (@external-adr GENESIS-0032-match-result-blob-carries-expertise-atoms): das ganze Kompetenz-Inventar des Teams, nicht nur die
// gefragten Skills. Beantwortet "was kann das Team wirklich, und welche Kompetenz ruht auf einer
// Person". Blendet sich aus, wenn der Blob kein Inventar trägt (RANK, Alt-Blobs, kein geladenes Fachwissen).
export function SkillStructurePanel({ view }: { view: TeamResultView }) {
  const intl = useIntl();
  // Eine Kompetenz, die genau eine Person hat und die auch nicht stark: das ist eine Zeile im
  // Profil dieser Person, keine Aussage ueber das Team. Sie stand nur da, um die Liste zu fuellen.
  // Die volle Liste (inkl. dieser Zeilen) steht in der Fachkompetenz-Matrix.
  const coverage = useMemo(
    () => buildSkillCoverage(view.factSkill).filter((c) => !(c.total === 1 && c.strong === 0)),
    [view.factSkill],
  );
  if (coverage.length === 0) return null;
  const teamSize = view.factSkill?.teamMembers.length ?? 0;
  const strongLevelName = intl.formatMessage({ id: `skill.levelName.${SKILL_STRONG_LEVEL}` });
  const seg = (n: number) => (teamSize > 0 ? Math.min(100, (n / teamSize) * 100) : 0);
  return (
    <div className="box bg-surface p-5">
      <BoxHeader
        title={<FormattedMessage id="match.result.skillStructureHeader" defaultMessage="Top Skills" />}
        right={
          <span className="text-smycles-label text-ink-muted">
            <FormattedMessage
              id="match.result.skillStrongHint"
              defaultMessage="stark ab {level}"
              values={{ level: strongLevelName }}
            />
          </span>
        }
      />
      {/* Das ganze Inventar, scrollend statt gekappt - wie die Powers-Liste. Eine Top-8-Kappung
          mit "+28 weitere" versteckt genau die Kompetenzen, die man hier sucht. */}
      <div className="mt-3 max-h-[17rem] space-y-2.5 overflow-y-auto pr-1 text-smycles-meta">
        {coverage.map((c) => {
          // Tiefe neben der Breite: wie viele haben die Kompetenz überhaupt, und wie hoch reicht
          // sie im Team. Ohne beides lesen sich "1 stark, 1 überhaupt" und "1 stark, 4 überhaupt"
          // gleich – völlig verschiedene Risiken (spark #236).
          const parts = [
            intl.formatMessage(
              { id: "match.result.skillStrongOfTeam", defaultMessage: "{n} von {total} stark" },
              { n: c.strong, total: teamSize },
            ),
          ];
          // Immer, auch wenn total === strong ("3 von 7 stark · 3 überhaupt"). Weglassen sparte
          // eine redundante Zahl, machte aber die Zeilenform von den Daten abhaengig: die fehlende
          // Angabe war selbst eine Aussage, die man nur mit Kenntnis der Regel lesen konnte.
          parts.push(
            intl.formatMessage(
              { id: "match.result.skillTotalAtAll", defaultMessage: "{n} überhaupt" },
              { n: c.total },
            ),
          );
          // Stufennamen gibt es nur fuer 1..7; ausserhalb (kaputte Daten) lieber nichts als die Key-ID.
          if (c.maxLevel > 0 && c.maxLevel <= LEVEL_COUNT) {
            parts.push(
              intl.formatMessage(
                { id: "match.result.skillMaxLevel", defaultMessage: "bis {level}" },
                { level: intl.formatMessage({ id: `skill.levelName.${c.maxLevel}` }) },
              ),
            );
          }
          return (
            <div key={c.skill} className="min-w-0">
              <div className="flex items-baseline justify-between gap-3">
                <span className="flex min-w-0 items-baseline gap-1.5">
                  <span className="min-w-0 truncate text-ink">{c.skill}</span>
                  {c.requested && (
                    <span className="shrink-0 rounded-full bg-ink/[0.06] px-2 py-0.5 text-smycles-micro text-ink">
                      <FormattedMessage id="match.result.skillRequested" defaultMessage="Anforderung" />
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-smycles-label tabular-nums text-ink-muted">
                  {parts.join(" · ")}
                </span>
              </div>
              {/* Gestapelt wie die Anforderungszeile: stark (voller Akzent) + der Rest, der sie
                  überhaupt hat (Akzent gedimmt) - beide Segmente in Signalfarbe, damit sie als
                  eine Skala lesen; die Intensität traegt weiterhin stark/ueberhaupt auseinander. */}
              <div className="mt-1.5 flex h-[3px] w-full overflow-hidden rounded bg-ink/10">
                <div className="h-full" style={{ width: `${seg(c.strong)}%`, background: "var(--color-accent)" }} />
                <div className="h-full bg-accent/30" style={{ width: `${seg(c.total - c.strong)}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Fachkompetenz-Matrix als Personen-Achse statt Grid oder Balken-Segmente: jede Zeile ist ein
// Kompetenz-Name plus eine Achse von "keine Stufe" (links) bis zur hoechsten Stufe (rechts), auf
// der jede Person als Avatar an ihrer tatsaechlichen Stufe sitzt. Zeigt Identitaet UND Stufe in
// einem Blick, ohne Klick - eine reine Zahl oder ein Balken-Segment (Vorgaenger-Versionen dieser
// Zeile) verschluckten entweder die Verteilung oder die Namen. Kein Expand/Collapse mehr noetig,
// die Antwort steht schon da (spark #236 Nachfolge).
const SKILL_LEVEL_AXIS_PADDING_PERCENT = 8;
// Face-Pile-Deckel pro Stufe: mehr als das wird ein "+N"-Chip statt die Achse zu sprengen. Analog
// zu RequirementMatrixDiagram.LANE_MIN_GAP_PERCENT - dokumentierte Grenze, kein Anspruch auf
// beliebig grosse Haeufungen auf derselben Stufe.
const SKILL_AVATAR_STACK_CAP = 5;

function skillLevelPercent(level: number): number {
  const ratio = level / LEVEL_COUNT;
  return SKILL_LEVEL_AXIS_PADDING_PERCENT + ratio * (100 - 2 * SKILL_LEVEL_AXIS_PADDING_PERCENT);
}

function skillLevelLabel(intl: IntlShape, level: number): string {
  return level > 0
    ? intl.formatMessage({ id: `skill.levelName.${level}` })
    : intl.formatMessage({ id: "match.result.skillMatrixUnrated", defaultMessage: "vorhanden, keine Stufe" });
}

// Personenliste in einem Klick-Popover (Avatar + Name, klickbar zum Profil ueber PersonResultRows
// eigenen Link-Default) - dasselbe Portal-Muster wie RequirementMatrixDiagram.ContributorPopover.
// Bewusst dupliziert statt extrahiert: Janeks Datei ist mitten in einem eigenen MR, eine
// gemeinsame Extraktion waere ein dritter, unabhaengiger Schritt (Refactoring-Fund, siehe PR).
function ContributorPopoverList({ people }: { people: ResultPerson[] }) {
  return (
    <div className="space-y-1">
      {people.map((p) => (
        <PersonResultRow key={p.id} person={p} title={p.name} embedded />
      ))}
    </div>
  );
}

function SkillContributorButton({
  people,
  className,
  children,
}: {
  people: ResultPerson[];
  className: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPosition({ top: rect.bottom + window.scrollY + 8, left: rect.left + window.scrollX + rect.width / 2 });
  }, [open]);
  return (
    <span className="relative inline-flex">
      <button ref={buttonRef} type="button" aria-expanded={open} onClick={() => setOpen((v) => !v)} className={className}>
        {children}
      </button>
      {open && position
        ? createPortal(
            <>
              <span role="presentation" onClick={() => setOpen(false)} className="fixed inset-0 z-20 cursor-default" />
              <div
                className="bg-page box-flat absolute z-30 max-h-80 w-80 -translate-x-1/2 overflow-y-auto rounded-2xl p-2 text-left shadow-lg"
                style={{ top: position.top, left: position.left }}
              >
                <ContributorPopoverList people={people} />
              </div>
            </>,
            document.body,
          )
        : null}
    </span>
  );
}

// Zeile der "Team Fokus"-Ansicht (eine Kompetenz, die Stufen-Achse zeigt, wer im Team sie wie gut
// traegt). Angeforderte Kompetenzen (im Anforderungsprofil verlangt) tragen das "Anforderung"-Badge
// aus SkillStructurePanel wieder - dieselbe Kennzeichnung, damit man sie querlesen kann.
function TeamFocusRow({
  row,
  teamSize,
  personById,
  intl,
}: {
  row: SkillCoverage;
  teamSize: number;
  personById: Map<string, ResultPerson>;
  intl: IntlShape;
}) {
  // Nach Stufe gruppieren (0 = keine Stufe), absteigend - staerkste Cluster liest man zuerst;
  // innerhalb einer Stufe alphabetisch, damit der Face-Pile nicht bei jedem Re-Render "springt".
  const byLevel = useMemo(() => {
    const groups = new Map<number, ResultPerson[]>();
    for (const [personId, level] of Object.entries(row.levelByPersonId)) {
      const person = personById.get(personId);
      if (!person) continue;
      const list = groups.get(level) ?? [];
      list.push(person);
      groups.set(level, list);
    }
    for (const list of groups.values()) list.sort((a, b) => a.name.localeCompare(b.name));
    return [...groups.entries()].sort((a, b) => b[0] - a[0]);
  }, [row.levelByPersonId, personById]);

  return (
    <div className="flex items-center gap-3 border-t border-ink/5 px-3 py-2">
      <div className="flex w-44 shrink-0 items-center gap-1.5 overflow-hidden">
        <span className="min-w-0 truncate text-smycles-meta font-medium">{row.skill}</span>
        {row.requested && (
          <span className="shrink-0 rounded-full bg-ink/[0.06] px-2 py-0.5 text-smycles-micro text-ink">
            <FormattedMessage id="match.result.skillRequested" defaultMessage="Anforderung" />
          </span>
        )}
      </div>
      <div className="relative h-7 flex-1">
        <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-ink/8" aria-hidden="true" />
        {/* Stufen-Markierungen: acht senkrechte Striche (0 = keine Stufe .. 7 = Pioneer) statt
            einer blanken Linie - macht sichtbar, dass die Achse diskrete Stufen traegt statt einer
            stufenlosen Skala. */}
        {Array.from({ length: LEVEL_COUNT + 1 }, (_, level) => (
          <div
            key={level}
            className="absolute top-1/2 h-2.5 w-px -translate-x-1/2 -translate-y-1/2 bg-ink/20"
            style={{ left: `${skillLevelPercent(level)}%` }}
            aria-hidden="true"
          />
        ))}
        {byLevel.map(([level, people]) => {
          const visible = people.slice(0, SKILL_AVATAR_STACK_CAP);
          const overflow = people.slice(SKILL_AVATAR_STACK_CAP);
          return (
            <div
              key={level}
              className="absolute top-1/2 flex -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${skillLevelPercent(level)}%` }}
            >
              {visible.map((person, i) => (
                <Tooltip key={person.id} content={`${person.name} · ${skillLevelLabel(intl, level)}`}>
                  <Link
                    to={`/p/${person.id}`}
                    className={`block h-7 w-7 rounded-full ring-2 ring-page transition hover:ring-accent-decoration/40 ${i > 0 ? "-ml-2.5" : ""}`}
                    style={{ zIndex: visible.length - i }}
                  >
                    <Avatar person={person} size="sm" />
                  </Link>
                </Tooltip>
              ))}
              {overflow.length > 0 && (
                <SkillContributorButton
                  people={overflow}
                  className="-ml-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-ink text-smycles-micro font-medium text-ink ring-2 ring-page transition hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-ink-strong"
                >
                  +{overflow.length}
                </SkillContributorButton>
              )}
            </div>
          );
        })}
      </div>
      <span className="w-14 shrink-0 text-right tabular-nums text-smycles-label text-ink-muted">
        <FormattedMessage
          id="match.result.structureOfTeam"
          defaultMessage="{n} von {total}"
          values={{ n: row.total, total: teamSize }}
        />
      </span>
    </div>
  );
}

function TeamFocusBoard({
  rows,
  teamSize,
  personById,
  intl,
}: {
  rows: SkillCoverage[];
  teamSize: number;
  personById: Map<string, ResultPerson>;
  intl: IntlShape;
}) {
  return (
    <div>
      {/* Achsen-Kopfzeile statt Wiederholung pro Zeile - die Spaltenbreiten (w-44 / flex-1 / w-14)
          muessen exakt zu TeamFocusRow passen, sonst driften Legende und Achse auseinander. Alle
          acht Stufennamen mit, nicht nur die beiden Enden - sonst bleiben die Tick-Striche in der
          Zeile darunter unbeschriftet. */}
      <div className="flex items-center gap-3 px-3 pb-2 text-smycles-micro tracking-[0.03em] text-ink-muted">
        <span className="w-44 shrink-0" />
        <div className="relative h-3.5 flex-1">
          {Array.from({ length: LEVEL_COUNT + 1 }, (_, level) => level).map((level) => (
            <span
              key={level}
              className={`absolute whitespace-nowrap ${
                level === 0 ? "left-0" : level === LEVEL_COUNT ? "right-0" : "-translate-x-1/2"
              }`}
              style={level > 0 && level < LEVEL_COUNT ? { left: `${skillLevelPercent(level)}%` } : undefined}
            >
              {skillLevelLabel(intl, level)}
            </span>
          ))}
        </div>
        <span className="w-14 shrink-0" />
      </div>
      {rows.map((r) => (
        <TeamFocusRow key={r.skill} row={r} teamSize={teamSize} personById={personById} intl={intl} />
      ))}
    </div>
  );
}

interface TeamLevelSkill {
  skill: string;
  people: ResultPerson[];
}

// "Skill Fokus"-Pivot: dieselben Rohdaten wie TeamFocusBoard (SkillCoverage.levelByPersonId), nur
// nach Stufe statt nach Kompetenz gruppiert - Stufe -> welche Kompetenzen haelt mindestens eine
// Person hier, mit genau diesen Personen. Ein Skill kann in mehreren Stufen-Zeilen auftauchen (je
// einmal pro Stufe, auf der ihn wirklich jemand haelt) - anders liesse sich nicht zeigen, dass
// z.B. Docker fuer eine Person Stufe 3, fuer eine andere Stufe 6 bedeutet.
function buildTeamLevelBoard(
  rows: SkillCoverage[],
  personById: Map<string, ResultPerson>,
): Map<number, TeamLevelSkill[]> {
  const requestedSkills = new Set(rows.filter((r) => r.requested).map((r) => r.skill));
  const byLevel = new Map<number, Map<string, ResultPerson[]>>();
  for (const row of rows) {
    for (const [personId, level] of Object.entries(row.levelByPersonId)) {
      const person = personById.get(personId);
      if (!person) continue;
      const bySkill = byLevel.get(level) ?? new Map<string, ResultPerson[]>();
      const people = bySkill.get(row.skill) ?? [];
      people.push(person);
      bySkill.set(row.skill, people);
      byLevel.set(level, bySkill);
    }
  }
  const result = new Map<number, TeamLevelSkill[]>();
  for (const [level, bySkill] of byLevel) {
    const list = [...bySkill.entries()].map(([skill, people]) => ({
      skill,
      people: [...people].sort((a, b) => a.name.localeCompare(b.name)),
    }));
    // Angeforderte Kompetenzen zuerst (Sonderfall - Anforderungen bleiben oben sichtbar, egal wie
    // viele Traeger sie sonst zaehlen), danach meistgetragen zuerst, wie buildSkillCoverage.
    list.sort(
      (a, b) =>
        Number(requestedSkills.has(b.skill)) - Number(requestedSkills.has(a.skill)) ||
        b.people.length - a.people.length ||
        a.skill.localeCompare(b.skill),
    );
    result.set(level, list);
  }
  return result;
}

// Chip im Look von SkillsDiagram/MatchSkillsDiagram.SkillTag (nicht importiert: jene Komponenten
// sind editierbar und an ihre eigenen Drag&Drop-/EntryFields-Vertraege gebunden; hier reicht die
// reine Optik). Klick oeffnet dieselbe Personenliste wie der Face-Pile-Ueberlauf im Team-Fokus.
function SkillFocusChip({ skill, people }: { skill: string; people: ResultPerson[] }) {
  return (
    <SkillContributorButton
      people={people}
      className="inline-flex max-w-full items-center gap-1.5 truncate rounded-full px-2.5 py-1 text-smycles-meta text-ink ring-1 ring-ink/15 transition hover:text-ink-accent hover:ring-accent-decoration/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui"
    >
      <span className="min-w-0 truncate">{skill}</span>
      <span className="shrink-0 tabular-nums text-smycles-micro text-ink-muted">{people.length}</span>
    </SkillContributorButton>
  );
}

function SkillFocusBoard({
  rows,
  personById,
  intl,
}: {
  rows: SkillCoverage[];
  personById: Map<string, ResultPerson>;
  intl: IntlShape;
}) {
  const accentColor = useThemePrefs((s) => s.accentColor);
  const board = useMemo(() => buildTeamLevelBoard(rows, personById), [rows, personById]);
  // Leere Stufen ausblenden statt wie im editierbaren Profil-Board immer alle 7 zu zeigen - hier
  // gibt es keinen Drop-Zweck fuer eine leere Zeile, nur Rauschen.
  const levelRows = [...SKILL_LEVELS].reverse().filter((l) => (board.get(l.value)?.length ?? 0) > 0);
  const unrated = board.get(0) ?? [];
  return (
    <div className="space-y-1">
      {levelRows.map((l) => {
        const swatch = skillLevelSwatch(l.value, accentColor);
        const levelName = intl.formatMessage({ id: l.nameId, defaultMessage: l.name });
        const skills = board.get(l.value) ?? [];
        return (
          <div
            key={l.value}
            className="flex items-center gap-3 rounded-md px-2 py-1.5"
            style={{ background: skillLevelBandBackground(l.value) }}
          >
            <div className="flex w-36 shrink-0 items-center gap-2">
              <span
                className="flex h-6 w-6 items-center justify-center rounded-md text-smycles-micro font-medium tabular-nums ring-1 ring-ink/10"
                style={{ background: swatch.background, color: swatch.textColor }}
              >
                {l.value}
              </span>
              <span className="min-w-0 truncate text-smycles-label text-ink">{levelName}</span>
            </div>
            <div className="flex min-h-6 flex-1 flex-wrap items-center gap-1.5">
              {skills.map(({ skill, people }) => (
                <SkillFocusChip key={skill} skill={skill} people={people} />
              ))}
            </div>
          </div>
        );
      })}
      {unrated.length > 0 ? (
        <div className="mt-1 flex items-center gap-3 rounded-md px-2 py-1.5">
          <div className="w-36 shrink-0 text-smycles-label text-ink-muted">{skillLevelLabel(intl, 0)}</div>
          <div className="flex min-h-6 flex-1 flex-wrap items-center gap-1.5">
            {unrated.map(({ skill, people }) => (
              <SkillFocusChip key={skill} skill={skill} people={people} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function SkillMatrixBody({
  view,
  persons,
  focus,
}: {
  view: TeamResultView;
  persons: ResultPerson[];
  focus: "skill" | "team";
}) {
  const intl = useIntl();
  const rows = useMemo(() => buildSkillCoverage(view.factSkill), [view.factSkill]);
  // Angeforderte Kompetenzen (im Anforderungsprofil verlangt) zuerst - Sonderfall, unabhaengig
  // davon, wie breit/tief das Team sie sonst traegt. Stabiler Sort: mischt buildSkillCoverage's
  // eigene Relevanz-Reihenfolge innerhalb "angefordert"/"nicht angefordert" nicht neu.
  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => Number(b.requested) - Number(a.requested)),
    [rows],
  );
  const personById = useMemo(() => new Map(persons.map((p) => [p.id, p])), [persons]);
  if (rows.length === 0) return <EmptyHint />;
  const teamSize = view.factSkill?.teamMembers.length ?? persons.length;
  return focus === "team" ? (
    <TeamFocusBoard rows={orderedRows} teamSize={teamSize} personById={personById} intl={intl} />
  ) : (
    <SkillFocusBoard rows={orderedRows} personById={personById} intl={intl} />
  );
}

// ─── Powers radar: scope-neutral building block (spark #257) ─────────────
//
// Team-Radar und Personen-Radar zeigen dieselbe Form gegen dieselbe Powers Scorecard - nur
// das Ist-Profil wechselt (Team-Mittel vs. Werte einer Person). Dieser Baustein kennt weder
// Team noch Person: er bekommt fertig aufbereitete Achsen (Werte, Ziele, Labels) fuer beide
// Skalen (Cluster, alle 30 Elemente) und rendert Kopf, Umschalter, Radar-Rahmen und Legende.
// Team- bzw. personen-spezifische Logik (Wunsch-Fit-Differenz, Abdeckungs-Hinweis, ...) bleibt
// bei den Aufrufern - der Baustein weiss nur, WIE ein Powers-Profil gezeichnet wird, nicht WESSEN.
interface PowerRadarAxisLabel {
  text: string;
  // Ist-Wert dieser Achse. Steht bei ausgeschriebenen Labels (Cluster) dauerhaft daneben, bei
  // Kuerzel-Labels nur unter dem Zeiger - zusammen mit dem Namen, fuer den das Kuerzel steht.
  value: number | null;
  // Was am Label keinen Platz hat (Gewichtung, "nicht bewertet"). Beim Cluster-Radar ein
  // <title>, beim Kuerzel-Radar ein aria-label: dort steht der Zeiger genau auf dem Label und
  // ein System-Tooltip deckte die Nachbarachsen zu.
  title?: string;
  // Ausgeschriebener Name, wo `text` nur ein Kuerzel ist. Tritt unter dem Zeiger an die
  // Stelle des Kuerzels; Cluster-Labels sind selbst ausgeschrieben und lassen das Feld leer.
  name?: string;
}

interface PowerRadarScopeData {
  values: Array<number | null>;
  // undefined = fuer diese Skala gibt es keine Scorecard zum Vergleich - dann bleiben die
  // gestrichelte Soll-Form UND die Legende ganz weg, statt eine leere Zielform zu zeigen.
  targets?: number[];
  labels: PowerRadarAxisLabel[];
}

// wishTarget/RADAR_ORIGIN liegen in match-result-derive.ts: Team- und Personen-Radar UND der
// PDF-Export brauchen dieselbe Ist/Wunsch-Abbildung (spark #257), und der PDF-Renderer ist ein
// eigenes Bundle, das ResultPanel nicht importieren kann. Beim Element-Radar ist das obere Ende
// ELEMENT_RADAR_MAX statt 100 - ohne diese Bindung klemmten alle Wuensche ab 7 am Aussenring.

function PowerRadarCard({
  title,
  actualLabel,
  hasData,
  note,
  cluster,
  element,
  bare = false,
  clusterLabelSize,
}: {
  title: ReactNode;
  // "Team · Ist" oder "Person · Ist" - die einzige Stelle, an der sich Team- und
  // Personen-Radar in ihrer Legende unterscheiden.
  actualLabel: ReactNode;
  // Ob es ueberhaupt ein Powers-Ergebnis fuer diesen Scope gibt (view.powers !== null).
  // Getrennt von leeren Werte-Arrays: einzelne fehlende Achsen zeichnen weiterhin ein
  // (kleineres) Radar, komplett fehlende Daten zeigen stattdessen den EmptyHint.
  hasData: boolean;
  // Zusatztext zwischen Kopf und Radar (Team: PowersBasisNote + Wunsch-Fit-Differenz).
  // Der Personen-Radar hat hier nichts zu zeigen.
  note?: ReactNode;
  cluster: PowerRadarScopeData;
  element?: PowerRadarScopeData;
  // Ohne eigene Box-Huelle, wenn ein Aufrufer die Karte selbst in eine gemeinsame Box mit
  // Nachbarinhalt einbettet (spark #257: Person-Radar + Cluster-Vergleich in EINER Box statt
  // zwei nebeneinander - zwei Rahmen mit Luecke dazwischen kostete beiden Seiten Breite).
  bare?: boolean;
  // RadarChart's Cluster-Default (9px) ist fuers Team-Radar kalibriert, das im Vollbreiten-Kasten
  // steht. Die Person-Karte hat dank `bare` und der festen 560px-Spalte mehr Luft - undefined
  // laesst RadarChart bei seinem Default, damit sich am Team-Radar nichts aendert.
  clusterLabelSize?: number;
}) {
  // Cluster ODER alle 30 Elemente. Das Cluster-Radar mittelt die fuenf Element-Wuensche
  // eines Clusters zu EINEM Ziel: Gewicht 10 neben Gewicht 0 ergibt Ziel 5, also "mittel
  // gewuenscht" fuer etwas, das niemand so gewuenscht hat. Die Element-Ansicht zeigt den
  // Wunsch ungemittelt.
  const [scope, setScope] = useState<"cluster" | "element">("cluster");
  const showElements = scope === "element" && !!element && element.values.length > 0;
  const activeScope = showElements && element ? element : cluster;
  return (
    <div className={bare ? "" : "box bg-surface p-5"}>
      <BoxHeader
        title={title}
        right={
          hasData && element && element.values.length > 1 ? (
            <SegmentedControl
              size="sm"
              value={scope}
              onChange={setScope}
              options={[
                {
                  value: "cluster",
                  label: <FormattedMessage id="match.result.radarScopeCluster" defaultMessage="Cluster" />,
                },
                {
                  value: "element",
                  label: (
                    <FormattedMessage
                      id="match.result.radarScopeElement"
                      defaultMessage="Alle {count} Powers"
                      values={{ count: element.values.length }}
                    />
                  ),
                },
              ]}
            />
          ) : null
        }
      />
      {!hasData ? (
        <EmptyHint />
      ) : (
        <div className="space-y-4">
          {note}
          {/* Feste Hoehe fuer BEIDE Radare: der Umschalter darf den Kasten nicht springen
              lassen. Der Wert ist die natuerliche Hoehe des hoeheren (Element-)Radars; das
              Cluster-Radar wird darin zentriert und behaelt seine Groesse. */}
          <div
            className="mx-auto flex w-full items-center justify-center"
            style={{ height: RADAR_FRAME_HEIGHT }}
          >
            {showElements && element ? (
              <RadarChart
                // Auf den Inhalt zugeschnitten statt auf die Cluster-Canvas: Labels sitzen bei
                // r + labelGap, also braucht die Zeichnung 2*(106+18) plus Luft fuer die 3
                // Zeichen am Rand.
                height={280}
                // Breiter als das Radar braucht: der Leerraum links und rechts gehoert zur
                // Zeichenflaeche und ist damit Trefferzone, in der der Zeiger stehen kann,
                // ohne die Beschriftung zu verdecken. Der Kasten waechst dadurch nicht - die
                // Hoehe bestimmt die Darstellungsgroesse, solange die Karte breit genug ist.
                width={420}
                // Der Achsabstand am Label-Ring waechst mit dem Radius: 2*PI*(r+labelGap)/30.
                // Bei r=106/gap=18 sind das 26px fuer ein 22px breites Kuerzel. Mit dem alten
                // r=92 stossen die Kuerzel in dieser Schriftgroesse am Scheitel aneinander -
                // groessere Schrift heisst hier zwingend groesseres Radar.
                r={106}
                labelGap={18}
                // Dieselbe Schriftgroesse wie die Cluster-Labels dieser Karte: das Radar
                // wechselt per Umschalter, die Beschriftung soll dabei nicht springen.
                labelSize={12}
                max={ELEMENT_RADAR_MAX}
                target={element.targets}
                values={element.values}
                labels={element.labels}
              />
            ) : (
              <RadarChart
                values={cluster.values}
                labels={cluster.labels}
                target={cluster.targets}
                labelSize={clusterLabelSize}
              />
            )}
          </div>
          {activeScope.targets != null && (
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-smycles-label text-ink-muted">
              <span className="flex items-center gap-1.5">
                <svg width="16" height="2" aria-hidden="true">
                  <line x1="0" y1="1" x2="16" y2="1" stroke="var(--color-accent)" data-accent-redundant strokeWidth="1.5" />
                </svg>
                {actualLabel}
              </span>
              <span className="flex items-center gap-1.5">
                <svg width="16" height="2" aria-hidden="true">
                  <line
                    x1="0"
                    y1="1"
                    x2="16"
                    y2="1"
                    stroke="var(--color-accent)" data-accent-redundant
                    strokeWidth="1.5"
                    strokeDasharray="4 3"
                    strokeOpacity="0.55"
                  />
                </svg>
                <FormattedMessage id="match.result.radarTarget" defaultMessage="Powers Scorecard" />
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function PowerClustersPanel({
  view,
  elementWeights,
  assessOnly = false,
}: {
  view: TeamResultView;
  elementWeights?: Readonly<Record<string, number>>;
  // Bewerten: das Team war vorgegeben, der Solver hat den Wunsch NICHT maximiert. Aendert nur
  // den Nachsatz unter der Differenz - die Zahl selbst ist in beiden Modi dieselbe Rechnung.
  assessOnly?: boolean;
}) {
  const intl = useIntl();
  const clusters = buildPowerClusters(view.powers);
  const wishes = buildClusterWishes(view.powers, elementWeights);
  const hasWishes = Math.max(0, ...wishes.values()) > 0;
  // EINE Zahl: die signierte Differenz zwischen gewichtetem und ungewichtetem Schnitt. Punkte,
  // kein Verhaeltnis - beide Mittel liegen auf derselben begrenzten 0-100-Skala, also ist die
  // Differenz schon die natuerliche Einheit. Ein Quotient wuerde eine Basis-Abhaengigkeit
  // erfinden, die die Skala nicht hat (+3 auf 68 und +3 auf 40 sind dieselben drei Punkte). Und
  // kein x/y: eine Gewichtung setzt keine Schwelle, es gibt hier keinen Nenner (spark #236).
  //
  // Vergleichbar ueber Matches, weil Bezug und Einheit in jedem Match dieselben sind: Punkte
  // gegenueber dem eigenen Schnitt ueber dieselben 30 Powers. Der ungewichtete Schnitt selbst
  // stand vorher als zweite Zahl daneben - er ist der Bezug, nicht die Aussage.
  const fit = buildWishFit(view.powers, elementWeights);
  const delta = formatDelta(fit.delta ?? 0);
  // UNIFORM schweigt wie NO_WISHES. Die Voreinstellung ("Ausgewogen") gewichtet alle 30 Powers
  // gleich, also traf die Zeile fast jedes Ergebnis - und sagte dabei etwas Falsches: gewichtet
  // ist dann ALLES, nur ohne Betonung. Die Differenz ist bei gleichen Gewichten zwangslaeufig 0,
  // es gibt also auch nichts zu erklaeren. Jede andere Flaeche haelt es schon so: Top Powers
  // blendet Gewichtungs-Spalte und Sortier-Umschalter aus (powerWishesUsable verlangt zwei
  // VERSCHIEDENE Gewichte), und die gestrichelte Soll-Form im Radar zeigt die Scorecard ohnehin.
  const fitEmphasis =
    fit.verdict === "NO_WISHES" || fit.verdict === "UNIFORM"
      ? null
      : intl.formatMessage(
            {
              id: "match.result.wishFitDelta",
              defaultMessage: "{delta} gegenüber dem Schnitt über alle 30 Powers",
            },
            { delta },
          );
  // Naheliegende Fehldeutung, wenn die Betonung nicht durchschlaegt: "falsch ausgewaehlt". Im
  // Optimieren-Modus ist das ausgeschlossen - die Auswahl hat genau diese Gewichte maximiert.
  // Im Bewerten-Modus wurde nie ausgewaehlt, dann waere der Satz falsch.
  const fitNote =
    !assessOnly && fit.verdict === "BELOW"
      ? intl.formatMessage({
          id: "match.result.wishFitPoolNote",
          defaultMessage: "mehr gab der Kandidatenpool nicht her",
        })
      : null;
  const elementStats = useMemo(() => buildElementStats(view.powers), [view.powers]);
  // Element-Reihenfolge nach Clustern, damit das 30-Achsen-Radar in Bloecken liest und
  // benachbarte Achsen inhaltlich zusammengehoeren.
  const elementOrder = useMemo(
    () => buildPowerColumnGroups(view.powers).flatMap((g) => g.elements),
    [view.powers],
  );

  const clusterScope: PowerRadarScopeData = {
    values: clusters.map((c) => c.score),
    targets: hasWishes ? clusters.map((c) => wishTarget(wishes.get(c.key) ?? 0, 100)) : undefined,
    labels: clusters.map((c) => ({
      text: intl.formatMessage({ id: powerGroupLabelId(c.key) }),
      value: c.score == null ? null : Math.round(c.score),
      title:
        c.score == null
          ? intl.formatMessage(
              {
                id: "match.result.radarTitleUnmeasured",
                defaultMessage:
                  "{count, plural, =0 {niemand im Team eingeschätzt} one {nur # Person eingeschätzt} other {nur # Personen eingeschätzt}}",
              },
              { count: c.assessedMembers },
            )
          : undefined,
    })),
  };

  const elementScope: PowerRadarScopeData | undefined =
    elementOrder.length > 0
      ? {
          // Gleiche Untergrenze wie beim Cluster-Radar: ein Element-Mittel aus einer
          // Person ist diese Person, und hier steht kein Nenner daneben, der das verriete.
          values: elementOrder.map((el) => {
            const st = elementStats.get(el);
            return st && st.assessed >= MEAN_MIN_MEMBERS ? st.mean : null;
          }),
          targets: elementWeights
            ? elementOrder.map((el) =>
                typeof elementWeights[el] === "number"
                  ? wishTarget(elementWish(elementWeights[el]), ELEMENT_RADAR_MAX)
                  : RADAR_ORIGIN,
              )
            : undefined,
          labels: elementOrder.map((el) => {
            // Dieselbe Untergrenze wie die gezeichnete Achse oben: eine Zahl am Label, wo die
            // Achse selbst "nicht gemessen" meint, waere ein Wert, den das Radar nicht zeigt.
            const st = elementStats.get(el);
            const mean = st && st.assessed >= MEAN_MIN_MEMBERS ? st.mean : null;
            const name = intl.formatMessage({
              id: elementNameId(el),
              defaultMessage: elementDisplayName(el),
            });
            const wish = elementWeights?.[el];
            return {
              text: ELEMENT_CODES[el] ?? el.slice(0, 3),
              name,
              value: mean == null ? null : Math.round(mean),
              title: [
                name,
                mean != null ? `${intl.formatMessage({ id: "match.result.radarTitleActual", defaultMessage: "Ist" })} ${Math.round(mean)}` : null,
                typeof wish === "number"
                  ? `${intl.formatMessage({ id: "match.result.radarTitleWish", defaultMessage: "Gewichtung" })} ${elementWish(wish).toFixed(1)}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · "),
            };
          }),
        }
      : undefined;

  return (
    <PowerRadarCard
      title={<FormattedMessage {...powerRadarSectionLabel(fit.weighted != null)} />}
      actualLabel={<FormattedMessage id="match.result.radarActual" defaultMessage="Team · Ist" />}
      hasData={view.powers !== null}
      note={
        <>
          {/* Das Radar nennt als einzige Powers-Flaeche gar keinen Nenner - hier ist die Basis
              also nicht bloss Kontext, sondern die einzige Stelle, an der sie ueberhaupt steht. */}
          <PowersBasisNote view={view} className="" />
          {/* Ohne gesetzte Gewichtung gibt es keine Differenz - dann entfaellt die Zeile ganz,
              statt den ungewichteten Schnitt als Zahl ohne Aussage stehen zu lassen. */}
          {fitEmphasis != null && (
            <div className="flex flex-wrap items-baseline gap-x-1.5 text-smycles-label tabular-nums text-ink-muted">
              <span className="min-w-0" data-testid="wish-fit-verdict">
                <span className="font-medium text-ink">{fitEmphasis}</span>
                {fitNote != null && <span> · {fitNote}</span>}
              </span>
              <InfoTooltip
                ariaLabel={intl.formatMessage({
                  id: "match.result.wishFitInfoAria",
                  defaultMessage: "Erklärung zur Gewichtung anzeigen",
                })}
                title={
                  <FormattedMessage
                    id="match.result.wishFitInfoTitle"
                    defaultMessage="Was hier verglichen wird"
                  />
                }
                placement="bottom"
              >
                {/* Der ungewichtete Schnitt ist nur noch Bezugspunkt, nicht mehr Anzeige - drei Saetze
                    ueber seine Eigenschaften erklaerten eine Zahl, die hier keine mehr ist. Und: dieses
                    Panel rendert in BEIDEN Modi. "Die Auswahl hat diese Gewichte maximiert" stimmt nur
                    im Optimieren-Modus - im Bewerten-Modus war das Team vorgegeben. Der Satz steht
                    deshalb als fitNote unten, wo assessOnly ihn abschalten kann (spark #236). */}
                <FormattedMessage
                  id="match.result.wishFitInfoBody"
                  defaultMessage="In der Powers Scorecard legst du fest, welche Powers dir wichtig sind (0–10). Die Zahl sagt, ob das Team genau dort über oder unter seinem eigenen Schnitt über alle 30 Powers liegt; Powers mit Gewichtung 0 zählen nicht mit. Eine Gewichtung ist keine Anforderung: sie lenkt die Auswahl, setzt aber keine Schwelle – deshalb gibt es hier kein Erfüllt und keine Lücke. Wer welche Power stark mitbringt, steht in „Top Powers“."
                />
              </InfoTooltip>
            </div>
          )}
        </>
      }
      cluster={clusterScope}
      element={elementScope}
    />
  );
}

// ─── Power-Fit-Tab einer Person im RANK-Modus (spark #257) ────────────────
//
// Dieselbe Powers Scorecard wie im Team-Radar, aber als Ist-Profil EINER Person statt des
// Team-Mittels: Radar, Achsen-Neigung, Drilldown. Bewusst keine Team- oder Sollvergleiche im
// Drilldown - die Scorecard ist ein Wunsch-/Gewichtungsprofil, kein "erfuellt/verfehlt".
// MEAN_MIN_MEMBERS gilt nicht: das ist eine Schwelle fuer Team-Mittelwerte, nicht fuer die
// direkten Werte einer einzelnen Person.
function CandidatePowerFitDetail({
  person,
  view,
  elementWeights,
}: {
  person: ResultPerson;
  view: TeamResultView;
  elementWeights?: Readonly<Record<string, number>>;
}) {
  const intl = useIntl();
  const detail = useMemo(
    () => buildMemberDetail(view.factSkill, view.powers, person.id, elementWeights),
    [view.factSkill, view.powers, person.id, elementWeights],
  );
  const groups = useMemo(() => buildPowerColumnGroups(view.powers), [view.powers]);
  const elementOrder = useMemo(() => groups.flatMap((g) => g.elements), [groups]);
  const wishes = useMemo(
    () => buildClusterWishes(view.powers, elementWeights),
    [view.powers, elementWeights],
  );
  const hasWishes = Math.max(0, ...wishes.values()) > 0;

  if (!detail) return <EmptyHint />;

  const cellByElement = new Map(detail.cells.map((c) => [c.element, c.value]));
  // Referenz fuer die Cluster-Balken rechts: c.teamScore, das Cluster-Mittel ueber ALLE
  // Kandidaten dieser Rangliste (view.powers.teamMembers ist im RANK-Modus der ganze Pool, nicht
  // ein ausgewaehltes Team - buildMemberDetail berechnet teamScore bereits genau darueber).
  // Dasselbe Feld, dieselbe Formel wie "Powers im Teamvergleich" in MemberDetailPanel - "wie
  // steht diese Person zu den anderen Kandidaten" ist hier die gefragte Aussage, nicht "wie
  // steht sie zu sich selbst" (das war eine fruehere Version dieser Karte, review spark #257).
  const poolClusters = detail.clusters.filter((c) => c.score != null && c.teamScore != null);
  const poolAboveCount = poolClusters.filter((c) => (c.score as number) > (c.teamScore as number)).length;
  const notAssessed = intl.formatMessage({
    id: "match.result.radarTitleUnmeasuredPerson",
    defaultMessage: "nicht bewertet",
  });

  const clusterScope: PowerRadarScopeData = {
    values: detail.clusters.map((c) => c.score),
    targets: hasWishes ? detail.clusters.map((c) => wishTarget(wishes.get(c.key) ?? 0, 100)) : undefined,
    labels: detail.clusters.map((c) => ({
      text: intl.formatMessage({ id: powerGroupLabelId(c.key) }),
      value: c.score == null ? null : Math.round(c.score),
      title: c.score == null ? notAssessed : undefined,
    })),
  };

  const elementScope: PowerRadarScopeData | undefined =
    elementOrder.length > 0
      ? {
          values: elementOrder.map((el) => cellByElement.get(el) ?? null),
          targets: elementWeights
            ? elementOrder.map((el) =>
                typeof elementWeights[el] === "number"
                  ? wishTarget(elementWish(elementWeights[el]), ELEMENT_RADAR_MAX)
                  : RADAR_ORIGIN,
              )
            : undefined,
          labels: elementOrder.map((el) => {
            const value = cellByElement.get(el) ?? null;
            const name = intl.formatMessage({
              id: elementNameId(el),
              defaultMessage: elementDisplayName(el),
            });
            return {
              text: ELEMENT_CODES[el] ?? el.slice(0, 3),
              name,
              value: value == null ? null : Math.round(value),
              title: [
                name,
                value != null
                  ? `${intl.formatMessage({ id: "match.result.radarTitleActual", defaultMessage: "Ist" })} ${Math.round(value)}`
                  : notAssessed,
              ].join(" · "),
            };
          }),
        }
      : undefined;

  // Cluster-Zeilen rechts: Balken ohne Zahl-Delta daneben (dasselbe Prinzip wie
  // "Powers im Teamvergleich" in MemberDetailPanel - ein signiertes Delta als Text UND als
  // Balken waere dieselbe Aussage zweimal kodiert). Der Absolutwert am Ende ist der einzige Text.
  // Braucht mehr als diese eine Person im Pool, sonst IST der Kandidaten-Schnitt die Person selbst.
  const showClusterCompare = view.powers !== null && (view.powers?.teamMembers.length ?? 0) > 1;
  const hasTilts = detail.tilts.length > 0;
  // Bis zu drei Spalten (Radar | Cluster-Vergleich | Achsen-Neigung) statt die Neigung separat
  // unter beiden zu haengen (review: dort ging sie visuell unter). Vollstaendige, statische
  // Klassennamen pro Kombination statt einer zusammengesetzten Grid-Template-Zeichenkette -
  // Tailwinds JIT-Scanner braucht den Klassennamen wortwoertlich im Quelltext, ein dynamisch
  // gebauter String waere unsichtbar fuer ihn.
  //
  // minmax(0,1fr) und minmax(220px,auto) liessen die Cluster-Namen-Spalte bei Containerbreiten
  // knapp ueber dem Breakpoint auf 0 kollabieren: 560px + eine wachsende auto-Spalte konnten
  // mehr beanspruchen, als beim Umschlagpunkt tatsaechlich da war, und die Namen (minmax(0,1fr)
  // INNERHALB der Zeile) verloren dabei zuerst - Balken und Zahl haben feste rem-Breiten, der
  // Name war die einzige elastische Spalte (review, responsive kaputt). Jetzt: eine begrenzte
  // Bandbreite je Spalte (min UND max), damit weder eine Spalte kollabiert noch eine andere
  // auf sehr breiten Bildschirmen unbegrenzt auffranst - und ein Breakpoint, der zur tatsaechlich
  // benoetigten Summenbreite der Minimalwerte passt (560 + 360 + 280 + 2 Gaps ≤ @7xl).
  const gridClass =
    showClusterCompare && hasTilts
      ? "grid gap-8 @7xl:grid-cols-[560px_minmax(360px,460px)_minmax(280px,360px)]"
      : showClusterCompare
        ? "grid gap-8 @6xl:grid-cols-[560px_minmax(360px,460px)]"
        : hasTilts
          ? "grid gap-8 @6xl:grid-cols-[560px_minmax(280px,360px)]"
          : "";
  // Muss denselben Breakpoint treffen wie gridClass, sonst erscheint die Trennlinie/das Padding,
  // bevor (oder nachdem) das Grid tatsaechlich auf Spalten umgeschaltet hat.
  const sideColClass =
    showClusterCompare && hasTilts
      ? "@7xl:border-l @7xl:border-ink/10 @7xl:pl-8"
      : "@6xl:border-l @6xl:border-ink/10 @6xl:pl-8";

  return (
    // min-w-0: ohne das kann dieser Block nicht unter seine intrinsische Breite schrumpfen (die
    // harten minmax()-Mindestbreiten im Grid unten addieren sich auf ~1264px). Das zwingt nicht
    // nur die Karte, sondern die ganze umgebende Kandidatenzeile inkl. der Badges oben zum
    // horizontalen Overflow - und ohne min-w-0 misst der @container-Kontext nie die tatsaechlich
    // schmale Viewport-Breite, wodurch der Fallback auf eine gestapelte Spalte (@6xl/@7xl) gar
    // nicht erst greift (review, "responsive noch nicht ganz funktional").
    <div className="min-w-0 max-w-[90rem] space-y-4 py-2" data-testid="candidate-power-fit-detail">
      {/* Keine .box-Huelle mehr (review): auf dem Seitenhintergrund reicht die Gruppierung
          durch Abstand und die Trennlinien zwischen den Spalten - ein Rahmen war hier nur Chrome.
          Feste 560px fuer die Radar-Spalte: das Cluster-Radar zeichnet selbst mit fester Breite,
          ein 1fr wuerde es stauchen. Unter @6xl faellt alles auf gestapelte Bloecke zurueck. */}
      <div className={gridClass}>
        <PowerRadarCard
          title={<FormattedMessage id="match.result.ranking.kpiPowerFit" defaultMessage="Power-Fit" />}
          actualLabel={<FormattedMessage id="match.result.radarActualPerson" defaultMessage="Person · Ist" />}
          hasData={view.powers !== null}
          note={
            <div className="text-smycles-title text-ink-muted">
              <FormattedMessage
                id="match.result.ranking.powerFitRadarExplainer"
                defaultMessage="Ist-Profil dieser Person gegenüber der Powers Scorecard des Matches."
              />
            </div>
          }
          cluster={clusterScope}
          element={elementScope}
          bare
          clusterLabelSize={12}
        />
        {showClusterCompare && (
          <div className={sideColClass} data-testid="candidate-power-cluster-compare">
            {/* smycles-field-label (11px eyebrow) statt eines BoxHeader-Titels (18px): ohne eigene Box
                ist das hier ein Unterabschnitt, kein Panel - dasselbe Muster wie "Powers im
                Teamvergleich" in MemberDetailPanel (UI_CONVENTIONS: Section-Headings gehoeren in
                eine Box; ausserhalb ist smycles-field-label die vorgesehene Stufe). */}
            <div className="smycles-field-label mb-1.5">
              <FormattedMessage
                id="match.result.ranking.powerFitClusterCompare"
                defaultMessage="Cluster im Vergleich"
              />
            </div>
            <div className="mb-1 text-smycles-body text-ink-muted">
              <FormattedMessage
                id="match.result.ranking.powerFitClusterCompareExplainer"
                defaultMessage="Cluster-Werte dieser Person gegenüber dem Schnitt aller Kandidaten in dieser Rangliste."
              />
            </div>
            <div className="mb-4 text-smycles-body text-ink-muted">
              <span className="font-medium text-ink" data-testid="power-fit-pool-summary">
                <FormattedMessage
                  id="match.result.ranking.powerFitPoolCompareSummary"
                  defaultMessage="{above} von {total} Clustern über dem Schnitt aller Kandidaten"
                  values={{ above: poolAboveCount, total: poolClusters.length }}
                />
              </span>
            </div>
            <div className="grid gap-y-3.5">
              {detail.clusters.map((c) => (
                <div
                  key={c.key}
                  className="grid grid-cols-[minmax(0,1fr)_5.5rem_2.25rem] items-center gap-3 text-smycles-body"
                >
                  {/* whitespace-nowrap statt truncate: die sechs Cluster-Namen sind eine feste,
                      bekannte Menge - "Kommunikationsfähigkeit" (DE) ist der laengste. Die Spalte
                      ist breit genug bemessen (minmax(360px,460px) aussen), das hier abzuschneiden
                      war eine Notloesung fuer zu wenig reservierten Platz, keine echte Kollision. */}
                  <span className="min-w-0 whitespace-nowrap">
                    {intl.formatMessage({ id: powerGroupLabelId(c.key) })}
                  </span>
                  {c.score != null && c.teamScore != null ? (
                    <DeltaBar delta={c.score - c.teamScore} redundant />
                  ) : (
                    <span />
                  )}
                  {/* Signiertes Delta statt Absolutwert (review): der Absolutwert steht schon im
                      Radar links, hier fehlte bislang die einzige Zahl, die dort NICHT steht -
                      wie weit ueber/unter dem Kandidaten-Schnitt. */}
                  <span className="text-right tabular-nums text-ink-muted">
                    {c.score != null && c.teamScore != null ? formatDelta(c.score - c.teamScore) : "–"}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {hasTilts && (
          <div className={sideColClass} data-testid="candidate-power-tilts">
            <div className="smycles-field-label mb-1.5">
              <FormattedMessage
                id="match.result.ranking.powerFitTiltsHeading"
                defaultMessage="Neigung"
              />
            </div>
            <div className="mb-6 text-smycles-body text-ink-muted">
              <FormattedMessage
                id="match.result.ranking.powerFitTiltsExplainer"
                defaultMessage="Wo diese Person zwischen den beiden Polen jeder Achse liegt."
              />
            </div>
            {/* Mehr Luft als die kompakte Cluster-Liste daneben (review: wirkte duenn befuellt) -
                zwei Achsen statt sechs Zeilen vertragen den grosszuegigeren Abstand. */}
            <div className="grid gap-y-10">
              {detail.tilts.map((t) => (
                <TiltReading key={t.axis} tilt={t} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// EINE Powers-Liste (spark #236). Vorher zwei Kaesten: "Top Powers" nach Ist-Mittel und
// "Wichtigste Powers" nach Gewichtung - dieselbe Zeile, nur anders sortiert. Also eine Liste mit
// einem Sortier-Schalter. Sie fuellt die Luecke, die Radar und Power-Fit offen lassen: das
// Cluster-Radar mittelt die Gewichtung weg, der Power-Fit presst sie in eine Zahl, und beide
// bleiben Mittel - 68 ist dort nicht von {91,87,51,55,59} zu unterscheiden.
//
// Kein Diagramm in den Zeilen. Balken, Punktreihe und Streuachse haben hier nacheinander
// dasselbe getan: sie haben die Personen-Ebene nachgezeichnet, die die Power-Scorecard-Matrix
// vollstaendig und exakt zeigt, und die Form, die das Radar daneben zeigt. Diese Liste hat nur
// drei Zahlen, die sonst nirgends nebeneinander stehen: was ich gewichtet habe, wie viele es
// stark mitbringen, wo das Team steht. Drei Zahlenspalten unter einer Legende muss man raten -
// deshalb tragen die Spalten ihre Beschriftung selbst, statt eines Hinweises im Kopf.
// WICHTIG: keine Erfuellungs-Sprache. Eine Gewichtung ist ein Objective-Term, keine Auflage - es
// gibt keine Schwelle, die gerissen werden kann, also kein Haken, kein Soll-Strich, keine
// "Luecke". Nur: hier lag Gewicht, so ist das Team dort gebaut.
export function PowersListPanel({
  view,
  persons,
  elementWeights,
}: {
  view: TeamResultView;
  persons: ResultPerson[];
  elementWeights?: Readonly<Record<string, number>>;
}) {
  const intl = useIntl();
  const all = useMemo(
    () => buildPowerRows(view.powers, elementWeights),
    [view.powers, elementWeights],
  );
  // Team-Score fuehrt: die Liste beantwortet zuerst "worin ist dieses Team stark". Nach
  // Gewichtung sortiert stand oben, was gewuenscht war - eine Reihenfolge, die der Nutzer
  // selbst gesetzt hat und die ueber das Team nichts sagt.
  const [sort, setSort] = useState<"wish" | "score">("score");
  const wishesUsable = powerWishesUsable(all);
  const byWish = wishesUsable && sort === "wish";
  const rows = byWish ? sortPowerRowsByWish(all) : all;
  if (rows.length === 0) return null;
  const personById = new Map(persons.map((p) => [p.id, p]));
  const hasSole = rows.some((r) => r.strong === 1);
  // Ein Raster fuer Kopf und Zeilen. Gedeckelt statt ueber die volle Kastenbreite gezogen: sonst
  // stehen Name und Zahlen so weit auseinander, dass man die Zeile beim Lesen verliert.
  const cols =
    "grid max-w-[34rem] grid-cols-[4.5rem_minmax(0,1fr)_3.75rem_2.25rem] gap-3 pr-1";
  return (
    <div className="box bg-surface p-5">
      <BoxHeader
        title={
          <span className="flex items-center gap-1.5">
            <FormattedMessage id="match.result.powersListHeader" defaultMessage="Top Powers" />
            {wishesUsable && (
              <InfoTooltip
                ariaLabel={intl.formatMessage({
                  id: "match.result.weightedPowersInfoAria",
                  defaultMessage: "Erklärung zu dieser Liste anzeigen",
                })}
                title={
                  <FormattedMessage
                    id="match.result.weightedPowersInfoTitle"
                    defaultMessage="Was hier steht"
                  />
                }
                placement="right"
              >
                <FormattedMessage
                  id="match.result.weightedPowersInfoBody"
                  defaultMessage="Alle 30 Powers des Teams, sortierbar nach Team-Score oder nach Gewichtung. Gewichtung: der Wert 0–10 aus der Powers Scorecard, mit dem die Auswahl gesteuert wurde. Ø: das Mittel des Teams bei dieser Power, 0–100."
                  values={{ threshold: POWER_STRONG_THRESHOLD }}
                />
              </InfoTooltip>
            )}
          </span>
        }
        right={
          wishesUsable ? (
            <SegmentedControl
              size="sm"
              value={sort}
              onChange={setSort}
              options={[
                {
                  value: "score",
                  label: <FormattedMessage id="match.result.powersSortScore" defaultMessage="Nach Team-Score" />,
                },
                {
                  value: "wish",
                  label: <FormattedMessage id="match.result.powersSortWish" defaultMessage="Nach Gewichtung" />,
                },
              ]}
            />
          ) : null
        }
      />
      <PowersBasisNote view={view} />
      <div className={`${cols} whitespace-nowrap border-b border-ink/10 pb-1 text-smycles-label text-ink-muted`}>
        <span className="truncate text-right">
          {wishesUsable ? (
            <FormattedMessage id="match.result.powersColWeight" defaultMessage="Gewichtung" />
          ) : null}
        </span>
        <span className="truncate">
          <FormattedMessage id="match.result.powersColPower" defaultMessage="Power" />
        </span>
        {/* Nur was die Zahl ist - warum die Schwelle bei 65 liegt, steht im ⓘ des Titels.
            Die Beschriftung ist der Ausloeser, kein zweites Symbol in der Kopfzeile. */}
        <span className="text-right">
          <Tooltip
            placement="top"
            content={
              <FormattedMessage
                id="match.result.powersColStrongInfo"
                defaultMessage="Wie viele im Team bei dieser Power über {threshold} liegen."
                values={{ threshold: POWER_STRONG_THRESHOLD }}
              />
            }
          >
            <span className="cursor-help">
              <FormattedMessage
                id="match.result.powersColStrong"
                defaultMessage="stark >{threshold}"
                values={{ threshold: POWER_STRONG_THRESHOLD }}
              />
            </span>
          </Tooltip>
        </span>
        <span className="text-right">Ø</span>
      </div>
      {/* Alle 30 Powers, scrollend statt abgeschnitten: eine Top-N-Kappung mit "+24 weitere"
          versteckt genau die Zeilen, die man in der anderen Sortierung sucht. Die Hoehe ist am
          Radar-Rahmen daneben ausgerichtet, damit die Reihe nicht kippt. */}
      <div className="mt-2 max-h-[17rem] space-y-1 overflow-y-auto text-smycles-meta">
        {rows.map((r) => {
          const sole =
            r.strong === 1 ? (personById.get(r.strongPersonIds[0] ?? "")?.name ?? null) : null;
          return (
            <div key={r.element} className={`${cols} items-baseline py-[3px]`}>
              {/* Links der Anker: die Gewichtung, wo eine Scorecard existiert (auch in der
                  Score-Sortierung - die Spalte behaelt ihre Bedeutung, sonst wandern beim
                  Umschalten Zahlen), sonst das Element-Kuerzel. */}
              <div className="text-right font-serif text-smycles-title leading-none tabular-nums text-ink">
                {wishesUsable ? (
                  r.wish == null ? (
                    <span className="text-ink-muted">–</span>
                  ) : (
                    r.wish.toFixed(1)
                  )
                ) : (
                  (ELEMENT_CODES[r.element] ?? r.element.slice(0, 3))
                )}
              </div>
              <div className="min-w-0 truncate text-ink">
                <FormattedMessage
                  id={elementNameId(r.element)}
                  defaultMessage={elementDisplayName(r.element)}
                />
                {sole ? (
                  <span className="text-ink-muted">
                    {" · "}
                    <FormattedMessage
                      id="match.result.analysis.soleCarrier"
                      defaultMessage="nur {name}"
                      values={{ name: sole }}
                    />
                  </span>
                ) : null}
              </div>
              <span
                className="text-right text-smycles-label tabular-nums text-ink-muted"
                title={intl.formatMessage(
                  { id: "match.result.structureOfTeam", defaultMessage: "{n} von {total}" },
                  { n: r.strong, total: r.assessed },
                )}
              >
                {`${r.strong}/${r.assessed}`}
              </span>
              <span className="text-right tabular-nums text-ink">{Math.round(r.mean)}</span>
            </div>
          );
        })}
      </div>
      {hasSole && (
        <div className="mt-2 text-smycles-label leading-snug text-ink-muted">
          <FormattedMessage
            id="match.result.weightedPowersSoleHint"
            defaultMessage="„nur …“ heißt: einzige Person, die diese Power stark mitbringt."
          />
        </div>
      )}
    </div>
  );
}

// Radar der Ist-Werte. Nullpunkt 50 statt 0 (RADAR_ORIGIN, siehe match-result-derive.ts): die
// Cluster-Scores sind Mittel aus bis zu 30 Selbsteinschaetzungen und landen dadurch praktisch
// immer im Band 60-75 - bei Nullpunkt 0 zeichnen zwei voellig verschiedene Teams dasselbe
// regulaere Sechseck. Der Nullpunkt ist am Ring beschriftet, damit die Stauchung offengelegt
// statt versteckt ist.

// Oberes Ende des Element-Radars. Element-Mittel eines Teams liegen real bei 50-80; gegen
// die theoretische 100 gezeichnet nutzt die Form ein Drittel des Radius und jedes Team
// sieht gleich klein aus. FEST statt aus den Daten abgeleitet, damit zwei Teams dieselbe
// Skala haben. Werte darueber klemmen am Aussenring - die exakte Zahl steht im Hover.
// ponytail: einziger Regler; hochsetzen, falls echte Teams oben anschlagen.
const ELEMENT_RADAR_MAX = 85;

// Reservierte Hoehe des Radar-Rahmens, gleich fuer Cluster- und Element-Ansicht. Ohne sie
// bestimmt die viewBox das Seitenverhaeltnis und damit die Hoehe - und der Kasten springt
// beim Umschalten (Cluster 560x220 -> flach, Elemente 260x230 -> hoch).
const RADAR_FRAME_HEIGHT = 280;

function RadarChart({
  values,
  labels,
  target,
  height = 220,
  width = 560,
  r = 68,
  labelSize = 9,
  labelGap = 14,
  max = 100,
}: {
  // Ist-Werte 0-100 (nicht vornormiert). null = fuer diese Achse liegt nichts vor: der Punkt
  // faellt aus dem Polygon, statt als Nullpunkt gezeichnet zu werden. Ein Wert am Nullpunkt
  // und "nicht gemessen" sind auf einem Radar sonst dieselbe Stelle.
  values: Array<number | null>;
  // Achsen-Beschriftung samt Ist-Wert - der gestauchte Radius ist nicht ablesbar,
  // die Zahl muss deshalb am Label stehen (und ist so auch fuer Screenreader da).
  // Bei 30 Achsen traegt sie nur, wessen Kuerzel sich gerade ausschreibt: 30 Zahlen
  // gleichzeitig waeren unlesbarer Text auf Text.
  labels: PowerRadarAxisLabel[];
  // Zielniveau je Achse, gleiche 0-100-Einheit wie `values` - sonst vergleicht das
  // Auge zwei Flaechen auf verschiedenen Skalen.
  target?: number[];
  // Geometrie waechst mit der Achsenzahl: 30 Achsen liegen 12 Grad auseinander, bei r=68
  // ueberlappen die Labels am Scheitel. Aufrufer gibt Hoehe/Radius vor.
  height?: number;
  // Canvas-Breite. 560 ist fuer die langen Cluster-Labels ("Kommunikationsfähigkeit 68"); das
  // Element-Radar nutzt 3-Zeichen-Codes und braucht kaum Seitenrand. Weil das Seitenverhaeltnis
  // die gerenderte Hoehe bestimmt, ist ueberzaehlige BREITE genau das, was den Kasten hoch macht.
  width?: number;
  r?: number;
  labelSize?: number;
  // Abstand der Beschriftung vom aeusseren Ring. Groessere Schrift braucht mehr, sonst klebt
  // das Label am Ring - und der Ring, auf dem die Labels sitzen, ist zugleich der, auf dem
  // sich ihr Abstand ZUEINANDER entscheidet.
  labelGap?: number;
  // Oberes Ende der Radialskala. 100 ist die theoretische Obergrenze, aber Element-Mittel
  // eines Teams liegen real bei 50-80: gegen 100 gezeichnet nutzt die Form ein Drittel des
  // Radius und jedes Team sieht gleich klein aus. Ein tieferes, FESTES Ende streckt die
  // Form und bleibt ueber Teams hinweg vergleichbar (ein aus den Daten abgeleitetes Ende
  // waere es nicht). Wird am Ring beschriftet, damit die Streckung offenliegt.
  max?: number;
}) {
  // Die Seiten-Labels ("Kommunikationsfähigkeit 68") sind breiter als das Sechseck selbst,
  // deshalb ist die Canvas viel breiter als hoch und der Radius fest statt aus der Hoehe
  // abgeleitet - sonst laeuft die Beschriftung aus der viewBox und wird abgeschnitten.
  // Welche Achse gerade ausgeschrieben ist (Zeiger, Finger oder Pfeiltaste). null = alle
  // tragen ihr Kuerzel.
  const [shown, setShown] = useState<number | null>(null);
  const intl = useIntl();
  // Eigene ID je Radar-Instanz: Team- und Personen-Radar stehen auf derselben Seite, und eine
  // doppelte clipPath-ID im Dokument laesst beide denselben Clip benutzen.
  const clipId = `radar-clip-${useId()}`;
  // Nur das Element-Radar hat Kuerzel mit einem Namen dahinter - das Cluster-Radar bleibt
  // eine Zeichnung ohne Verhalten und ohne Tabstopp.
  const spellable = labels.some((l) => l.name);
  const cx = width / 2;
  const cy = height / 2;
  const n = values.length;
  if (n === 0) return null;
  const angle = (i: number) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  // Kleiner Sockel statt 0, damit ein Team unter dem Nullpunkt nicht zum Punkt kollabiert
  // und "kaputt" aussieht.
  const radius = (score: number) =>
    r * Math.min(1, Math.max(0.06, (score - RADAR_ORIGIN) / (max - RADAR_ORIGIN)));
  const point = (i: number, score: number) => {
    const rr = radius(score);
    return [cx + rr * Math.cos(angle(i)), cy + rr * Math.sin(angle(i))];
  };
  // Nur die Achsen mit Wert spannen die Flaeche auf. Bei genau einer bleibt kein Polygon
  // uebrig - dann zeichnet die Komponente nur Ringe, Achsen und Labels.
  const polyPoints = values
    .map((v, i) => (v == null ? null : point(i, v).join(",")))
    .filter((p): p is string => p !== null)
    .join(" ");
  const rings = [0.25, 0.5, 0.75, 1];
  // Treffbereich einer ausschreibbaren Achse: ihr Kuchenstueck vom halben Radius bis hinter die
  // Beschriftung, als Vierecks-Naeherung des Kreissektors (bei 12 Grad liegt die Sehne 0,5%
  // innerhalb des Bogens). Ein Kasten eng um den Text zwingt den Zeiger auf genau den Text, den
  // er ausschreiben soll, und verdeckt ihn damit. Im Kuchenstueck kann er daneben stehen -
  // innen in der Zeichnung oder aussen hinter dem Label. Benachbarte Stuecke teilen sich ihre
  // Kante, es gibt also weder Loch noch Ueberlappung.
  const sector = (i: number) => {
    const half = Math.PI / n;
    const inner = r * 0.5;
    // Bis in die Ecken der Zeichenflaeche: der Leerraum neben dem Radar ist der einzige Ort,
    // an dem der Zeiger stehen kann, ohne die Beschriftung zu verdecken. Der Clip haelt die
    // Zone dabei INNERHALB der viewBox - ein Stueck, das darueber hinausragt, faengt sonst
    // die Klicks des Nachbarinhalts ab (im Personen-Panel steht rechts die Cluster-Spalte).
    const outer = Math.hypot(width, height);
    const at = (rad: number, a: number) => `${cx + rad * Math.cos(a)},${cy + rad * Math.sin(a)}`;
    const [a0, a1] = [angle(i) - half, angle(i) + half];
    // Bei einem so langen Stueck liegt die Sehne spuerbar innerhalb des Bogens, deshalb ein
    // Mittelpunkt auf der Achse selbst: aus dem Viereck wird ein Fuenfeck, das die Zone auch
    // in Achsrichtung bis an den Rand fuehrt.
    return [at(inner, a0), at(outer, a0), at(outer, angle(i)), at(outer, a1), at(inner, a1)].join(" ");
  };

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height="100%"
      // meet + xMidYMid: die Zeichnung passt sich in den Rahmen ein und bleibt zentriert,
      // statt ihn aufzuspannen. Damit haengt die Kastenhoehe am Rahmen, nicht an der viewBox.
      preserveAspectRatio="xMidYMid meet"
      // overflow-visible: ein ausgeschriebener Name ragt ueber die 260er-Canvas hinaus. Auf
      // breiten Karten faengt ihn der Leerraum links und rechts der Zeichnung auf, auf einer
      // schmalen (Telefon) nicht - dort wurde "Selbstwahrnehmung" um 7px beschnitten. Lieber
      // in das Kartenpolster hineinragen als abgeschnitten werden.
      className={`block overflow-visible${
        spellable ? " rounded outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui" : ""
      }`}
      // Eine Achse ausschreiben geht bisher nur mit Zeiger oder Finger. Das Radar ist EIN
      // Tabstopp (30 waeren eine Sperre mitten in der Seite), die Pfeiltasten gehen die Achsen
      // durch, Escape nimmt zurueck.
      tabIndex={spellable ? 0 : undefined}
      role={spellable ? "group" : undefined}
      aria-label={
        spellable
          ? intl.formatMessage({
              id: "match.result.radarKeyboardHint",
              defaultMessage: "Powers-Radar – mit den Pfeiltasten die Achsen durchgehen",
            })
          : undefined
      }
      onFocus={spellable ? () => setShown((cur) => cur ?? 0) : undefined}
      onBlur={spellable ? () => setShown(null) : undefined}
      onKeyDown={
        spellable
          ? (e) => {
              const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
              if (step !== 0) {
                e.preventDefault();
                setShown((cur) => ((cur ?? 0) + step + n) % n);
              } else if (e.key === "Escape") {
                setShown(null);
              }
            }
          : undefined
      }
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={width} height={height} />
        </clipPath>
      </defs>
      {rings.map((ring, idx) => (
        <polygon
          key={idx}
          points={Array.from({ length: n }, (_, i) => {
            const [x, y] = [cx + r * ring * Math.cos(angle(i)), cy + r * ring * Math.sin(angle(i))];
            return `${x},${y}`;
          }).join(" ")}
          fill="none"
          stroke="rgba(0,0,0,0.08)"
        />
      ))}
      {Array.from({ length: n }, (_, i) => {
        const [x, y] = [cx + r * Math.cos(angle(i)), cy + r * Math.sin(angle(i))];
        // Der Strahl der ausgeschriebenen Achse tritt mit hervor. Der Name allein sagt nicht,
        // WELCHE der 30 Achsen gemeint ist, und der Zeiger steht genau davor.
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={x}
            y2={y}
            stroke={i === shown ? "var(--color-accent)" : "rgba(0,0,0,0.08)"} data-accent-redundant
            strokeWidth={i === shown ? 2 : 1}
          />
        );
      })}
      <polygon
        points={polyPoints}
        fill="var(--color-accent)" data-accent-decoration
        fillOpacity="0.18"
        stroke="var(--color-accent)" data-accent-redundant
        strokeWidth="1.5"
      />
      {target && target.length === n && (
        <polygon
          points={target.map((v, i) => point(i, v).join(",")).join(" ")}
          fill="none"
          stroke="var(--color-accent)" data-accent-redundant
          strokeWidth="1.5"
          strokeDasharray="4 3"
        />
      )}
      {/* The opaque halo keeps text contrast independent of the polygon tint. */}
      {/* Nullpunkt beschriftet: ohne das ist der gestauchte Radius eine stille Uebertreibung. */}
      <text x={cx + 3} y={cy} fontSize={8} textAnchor="start" dominantBaseline="middle" className="fill-ink-muted" paintOrder="stroke" stroke="var(--context-fill)" strokeWidth="3">
        {RADAR_ORIGIN}
      </text>
      {/* Oberes Ende beschriftet, sobald es nicht die theoretische 100 ist - sonst waere die
          Streckung eine stille Uebertreibung, genau wie der gestauchte Nullpunkt. */}
      {max !== 100 && (
        <text x={cx + 3} y={cy - r} fontSize={8} textAnchor="start" dominantBaseline="middle" className="fill-ink-muted" paintOrder="stroke" stroke="var(--context-fill)" strokeWidth="3">
          {max}
        </text>
      )}
      {/* bd-requirements#26: 30 Achsen tragen nur ein 3-Zeichen-Kuerzel, und wer es nicht kennt,
          weiss nicht, welche Power gemeint ist. Unter dem Zeiger tritt der Name an seine Stelle -
          dort, wo der Blick ohnehin liegt, statt in einer Legende, die immer dasteht. Der
          Treffbereich ist ein unsichtbares Rechteck: das Kuerzel selbst misst nur 17x9 px.
          Der ausgeschriebene Name ist breiter als sein Kuerzel und deckt damit Nachbarn zu -
          die weichen, solange er steht (Text auf Text ist unlesbar), und das Label wird
          zuletzt gezeichnet, liegt also oben. Wessen Kasten nicht getroffen wird, bleibt
          unberuehrt: ein Name kostet nur die Beschriftung, die er wirklich verdeckt. */}
      {(() => {
        const boxes = labels.map(({ text, name, value }, i) => {
          const a = angle(i);
          const [lx, ly] = [cx + (r + labelGap) * Math.cos(a), cy + (r + labelGap) * Math.sin(a)];
          // Anchor labels toward the centre so long words stay inside the SVG.
          const cosA = Math.cos(a);
          const anchor: "start" | "end" | "middle" =
            cosA > 0.3 ? "start" : cosA < -0.3 ? "end" : "middle";
          const spelled = i === shown && name != null;
          const label = spelled ? name : text;
          // Die Zahl gehoert zum ausgeschriebenen Namen und steht nur unter dem Zeiger: 30 Werte
          // gleichzeitig waeren Text auf Text. Wo das Label seinen Namen ohnehin traegt
          // (Cluster-Radar, kein `name`), bleibt sie dauerhaft daneben.
          const valueShown = value != null && (name == null || spelled);
          // Der Kasten ist Treffbereich, nicht Text - auf dem Tablet wird getippt, nicht gezeigt,
          // und das Kuerzel selbst misst nur 17x9 px. Grenze ist der Achsabstand (bei 30 Achsen
          // 22 px): ein groesserer Kasten griffe ueber seinen Nachbarn, und getroffen wuerde
          // dann nicht die naechste Achse, sondern die zuletzt gezeichnete. Apples 44 px sind
          // hier also nicht zu haben. Das ausgeschriebene Label ist ausgenommen: sein breiter
          // Kasten haelt den Namen stehen, waehrend der Zeiger ihn entlangfaehrt.
          const reach = (2 * Math.PI * (r + labelGap)) / n;
          // Fett ist breiter als mager, und die Zahl haengt hinten dran: der Kasten muss BEIDES
          // decken, sonst verliert der Zeiger das Label am Textende und der Name springt zurueck.
          const box =
            (label.length + (valueShown ? String(value).length + 1 : 0)) *
              labelSize *
              (spelled ? 0.68 : 0.62) +
            10;
          const w = i === shown ? box : Math.min(box, reach);
          const h = Math.min(labelSize + 10, reach);
          return {
            i,
            label,
            valueShown,
            lx,
            ly,
            anchor,
            w,
            h,
            x: anchor === "start" ? lx - 4 : anchor === "end" ? lx - w + 4 : lx - w / 2,
          };
        });
        const front = shown == null ? null : boxes[shown]!;
        const covered = (b: (typeof boxes)[number]) =>
          front != null &&
          b.i !== front.i &&
          b.x < front.x + front.w &&
          front.x < b.x + b.w &&
          Math.abs(b.ly - front.ly) < (b.h + front.h) / 2;
        return [...boxes.filter((b) => b.i !== shown), ...(front ? [front] : [])].map((b) => {
          const { text, value, title, name } = labels[b.i]!;
          return (
            // Nur wo es etwas auszuschreiben GIBT: das Cluster-Radar traegt seine Namen schon,
            // dort bleibt das Label ein Stueck Zeichnung ohne Verhalten.
            <g
              key={text}
              data-axis={text}
              onMouseEnter={name ? () => setShown(b.i) : undefined}
              onMouseLeave={name ? () => setShown((cur) => (cur === b.i ? null : cur)) : undefined}
              // Ohne Zeiger (Tablet) gibt es kein Hover: dort schreibt der Tipp aus. Nur setzen,
              // nie zuruecknehmen - Safari schickt beim Tippen erst click, dann mouseenter, ein
              // Umschalter haette sich also selbst rueckgaengig gemacht. Der Name bleibt dann
              // stehen, bis die naechste Achse getippt wird; er verdeckt nur Nachbarkuerzel.
              onClick={name ? () => setShown(b.i) : undefined}
              // Wo sich das Kuerzel ausschreibt, traegt das Label selbst Name und Wert - ein
              // <title> daneben waere derselbe Text ein zweites Mal, als grauer System-Kasten
              // unter dem Zeiger, der genau die Nachbarachsen zudeckt. Die Zusatzinfo
              // (Gewichtung, "nicht bewertet") geht deshalb an die Screenreader, nicht in
              // einen Tooltip. Das Cluster-Radar behaelt seinen <title>: dort ist er die
              // EINZIGE Stelle, an der "nicht bewertet" steht.
              role={name && title ? "img" : undefined}
              aria-label={name && title ? title : undefined}
            >
              {title && !name ? <title>{title}</title> : null}
              {name ? (
                <polygon points={sector(b.i)} fill="transparent" clipPath={`url(#${clipId})`} />
              ) : null}
              {/* Der ausgeschriebene Name ragt weit ueber sein Kuchenstueck hinaus. Ohne diesen
                  Kasten verliert ihn der Zeiger genau dort, wo der Leser hinschaut. */}
              {name && b.i === shown ? (
                <rect x={b.x} y={b.ly - b.h / 2} width={b.w} height={b.h} fill="transparent" />
              ) : null}
              {!covered(b) && (
                <text
                  x={b.lx}
                  y={b.ly}
                  fontSize={labelSize}
                  textAnchor={b.anchor}
                  dominantBaseline="middle"
                  className={b.i === shown ? "fill-ink" : "fill-ink-muted"}
                  paintOrder="stroke"
                  stroke="var(--context-fill)"
                  strokeWidth="3"
                  // Fett unter dem Zeiger: der Zeiger steht auf dem Label, das er ausschreibt,
                  // und nimmt ihm einen Teil seiner Flaeche. Mager und grau war dort zu wenig.
                  fontWeight={b.i === shown ? 600 : undefined}
                >
                  {b.label}
                  {b.valueShown ? (
                    <tspan className="fill-ink" fontWeight={600}>{` ${value}`}</tspan>
                  ) : null}
                </text>
              )}
            </g>
          );
        });
      })()}
    </svg>
  );
}

function EmptyHint() {
  return (
    <div className="text-smycles-label text-ink-muted">
      <FormattedMessage
        id="match.result.emptyHint"
        defaultMessage="No data in the result."
      />
    </div>
  );
}

// ─── Solver error banner ─────────────────────────────────────────────────

interface SolverErrorInfo {
  message: string;
  correlationId?: string;
  errorId?: string;
}

export function extractSolverErrorInfo(err: unknown): SolverErrorInfo | null {
  if (!err) return null;
  const e = err as { message?: string; graphQLErrors?: Array<{ extensions?: Record<string, unknown> }> };
  const gqlErr = e.graphQLErrors?.[0];
  const ext = (gqlErr?.extensions ?? {}) as { correlationId?: string; errorId?: string };
  return {
    message: e.message ?? "Unknown error",
    correlationId: ext.correlationId,
    errorId: ext.errorId,
  };
}

// Fallback message intentionally kept untranslated (developer-facing only).

export function SolverErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const info = extractSolverErrorInfo(error);
  if (!info) return null;
  return (
    <div className="box-flat bg-ink/[0.06] p-5">
      <BoxHeader
        title={<FormattedMessage id="match.result.solverErrorTitle" defaultMessage="Solver error" />}
        right={
          onRetry ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onRetry}
            >
              <FormattedMessage id="common.tryAgain" defaultMessage="Try again" />
            </Button>
          ) : null
        }
      />
      <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3">
        <span
          className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent/15 text-smycles-micro font-semibold text-ink-accent"
          aria-hidden
        >
          !
        </span>
        <div>
          <div className="text-smycles-meta font-medium text-ink">
            <FormattedMessage
              id="match.result.solverErrorHeading"
              defaultMessage="The backend didn't finish the Solver."
            />
          </div>
          <div className="text-smycles-label leading-snug text-ink-muted">
            <FormattedMessage
              id="match.result.solverErrorBody"
              defaultMessage="Genesis rejected the calculation. This currently happens when the match design contains fields the backend doesn't (yet) know. Try again shortly or report the case with the correlation ID below."
            />
          </div>
          {(info.correlationId || info.errorId) && (
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 font-mono text-smycles-micro text-ink-muted">
              {info.correlationId ? <span>correlationId: {info.correlationId}</span> : null} {/* i18n-allow: technical token */}
              {info.errorId ? <span>errorId: {info.errorId}</span> : null} {/* i18n-allow: technical token */}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Infeasible solution banner ──────────────────────────────────────────

// Whether a culprit's shortfall counts members *over* a limit rather than members missing — the
// met-of-target reading would otherwise state the exact inverse of the truth. Two signals, because
// neither alone is enough: a MAX composition can only ever overshoot, and any shortfall larger than
// the target must contain overshoot whatever the operator. The latter is what catches EXACT, whose
// under- and overshoot slack the solver reports summed: a pure deficit is bounded by the target
// (`exp + under == target` with `exp >= 0`), so exceeding it is a reliable signal.
// A zero target carries no signal either way: everything exceeds it, and an ANY requirement reports
// one for a genuine deficit, so only an explicit MAX settles that case.
function isOvershoot({ condition, shortfall, target }: RelaxationView): boolean {
  if (readComposition(condition)?.operator === "MAX") return true;
  return shortfall != null && target != null && target > 0 && shortfall > target;
}

// Severity is the shortfall relative to what the requirement asked for, the same measure the solver
// minimises (@external-adr MATCH-SERVICE-0001-infeasibility-is-two-classes-and-a-conjunctive-repair-set): one
// member short of forty is a near miss, one short of one is unreachable, and the bare shortfall is 1
// either way. Deficits land in (0, 1]; an overshoot has no such ceiling, because the solver prices
// it against the team headroom this panel doesn't know, so it is capped to stay on the scale.
// ponytail: capping is a floor on the guesswork, not a price; pass the team size in to place an
// overshoot properly.
function relaxationSeverity(relaxation: RelaxationView): number {
  const { shortfall, target } = relaxation;
  if (shortfall == null) return 0;
  return Math.min(shortfall / Math.max(target ?? 1, 1), 1);
}

// Worst first. Where a capped overshoot ties with a deficit, the deficit leads: its severity is
// measured, the overshoot's is only bounded, and a requirement nothing in the pool satisfies is the
// one worth reading first.
function byDescendingSeverity(a: RelaxationView, b: RelaxationView): number {
  return (
    relaxationSeverity(b) - relaxationSeverity(a) ||
    Number(isOvershoot(a)) - Number(isOvershoot(b))
  );
}

// How far a culprit is off, stated against its target so a near miss and a total miss stop reading
// alike. Falls back to the bare shortfall when there is no target to state it against: none was
// sent, or it is zero, which is what a requirement row with a blank count becomes on the way to the
// solver. An overshoot still names the limit it broke, zero or not.
function RelaxationAmount({ relaxation }: { relaxation: RelaxationView }) {
  const { shortfall, target } = relaxation;
  if (shortfall == null || shortfall <= 0) return null;
  const overshoot = isOvershoot(relaxation);
  const statedAgainstTarget = target != null && (target > 0 || overshoot);
  return (
    <span className="shrink-0 tabular-nums text-ink-muted">
      {!statedAgainstTarget ? (
        <FormattedMessage
          id="match.result.infeasibleShortfall"
          defaultMessage="short by {shortfall}"
          values={{ shortfall }}
        />
      ) : overshoot ? (
        <FormattedMessage
          id="match.result.infeasibleOvershoot"
          defaultMessage="{over} over the limit of {target}"
          values={{ over: shortfall, target }}
        />
      ) : (
        <FormattedMessage
          id="match.result.infeasibleMet"
          defaultMessage="{met} of {target} met"
          values={{ met: target - shortfall, target }}
        />
      )}
    </span>
  );
}

export function InfeasibleBanner({
  onAdjustRequirements,
  onManagePeople,
  infeasibility,
}: {
  onAdjustRequirements?: () => void;
  onManagePeople?: () => void;
  infeasibility?: InfeasibilityView | null;
}) {
  const intl = useIntl();
  const labelLookup = useConditionLabelLookup();
  const formatters = useConditionLabelFormatters(intl);
  const isLocked = infeasibility?.infeasibilityClass === "LOCKED";
  // The culprit list is a repair set, not a menu: every entry has to be relaxed before a team
  // exists (@external-adr MATCH-SERVICE-0001-infeasibility-is-two-classes-and-a-conjunctive-repair-set). Worst relative miss first, so the requirement that is out of
  // reach leads the ones that are a member away.
  const relaxations = useMemo(
    () =>
      [...(infeasibility?.relaxations ?? [])].sort(byDescendingSeverity),
    [infeasibility],
  );

  return (
    <div className="box-flat bg-ink/[0.06] p-5">
      <BoxHeader
        title={
          isLocked ? (
            <FormattedMessage
              id="match.result.infeasibleLockedTitle"
              defaultMessage="Locked candidates block the solve"
            />
          ) : (
            <FormattedMessage
              id="match.result.infeasibleTitle"
              defaultMessage="No solution found"
            />
          )
        }
        right={
          isLocked ? (
            // Locked-caused offers both advertised remedies: unlock members (people step)
            // or raise the team size (challenge step).
            <div className="flex items-center gap-2">
              {onManagePeople ? (
                <Button variant="secondary" size="sm" onClick={onManagePeople}>
                  <FormattedMessage
                    id="match.result.unlockMembers"
                    defaultMessage="Unlock members"
                  />
                </Button>
              ) : null}
              {onAdjustRequirements ? (
                <Button variant="ghost" size="sm" onClick={onAdjustRequirements}>
                  <FormattedMessage
                    id="match.result.adjustTeamSize"
                    defaultMessage="Team size"
                  />
                </Button>
              ) : null}
            </div>
          ) : onAdjustRequirements ? (
            <Button variant="secondary" size="sm" onClick={onAdjustRequirements}>
              <FormattedMessage
                id="match.result.adjustRequirements"
                defaultMessage="Adjust requirements"
              />
            </Button>
          ) : null
        }
      />
      <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-3">
        <span
          className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent/15 text-smycles-micro font-semibold text-ink-accent"
          aria-hidden
        >
          !
        </span>
        <div>
          <div className="text-smycles-meta font-medium text-ink">
            {isLocked ? (
              <FormattedMessage
                id="match.result.infeasibleLockedHeading"
                defaultMessage="The locked members alone can't form a valid team."
              />
            ) : (
              <FormattedMessage
                id="match.result.infeasibleHeading"
                defaultMessage="The Solver couldn't compose a team that satisfies all requirements."
              />
            )}
          </div>
          <div className="text-smycles-label leading-snug text-ink-muted">
            {isLocked ? (
              <FormattedMessage
                id="match.result.infeasibleLockedBody"
                defaultMessage="Too many people are locked in, or the locked members already violate a requirement. Unlock some members or raise the team size, then run the Solver again."
              />
            ) : (
              <FormattedMessage
                id="match.result.infeasibleBody"
                defaultMessage="The conditions contradict each other or your candidate pool is too small. Remove hard filters or enlarge the pool, then start the Solver again."
              />
            )}
          </div>
          {!isLocked && relaxations.length > 0 && (
            <div className="mt-3">
              <div className="smycles-field-label mb-1.5">
                <FormattedMessage
                  id="match.result.infeasibleRelaxTitle"
                  defaultMessage="{count, plural, one {Relax this must criterion} other {Smallest fix: relax all of these must criteria}}"
                  values={{ count: relaxations.length }}
                />
              </div>
              {/* Capped width: at full banner width the label and its count sat a thousand pixels
                  apart and stopped reading as one row. (This theme's max-w-xs is 320px.) */}
              <ul className="max-w-xs space-y-1">
                {relaxations.map((r, i) => {
                  const mapped = r.condition
                    ? labelForCondition(r.condition, labelLookup, formatters)
                    : null;
                  const label =
                    mapped && mapped !== CONDITION_LABEL_UNKNOWN ? mapped : r.restrictionEnum;
                  return (
                    <li
                      key={`${r.restrictionEnum}-${i}`}
                      className="flex items-baseline justify-between gap-3 text-smycles-label"
                    >
                      <span className="text-ink">{label}</span>
                      <RelaxationAmount relaxation={r} />
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Data quality hints ──────────────────────────────────────────────────

interface DataQualityHint {
  key: string;
  label: Msg;
  body: Msg;
}

export function buildDataQualityHints(view: TeamResultView): DataQualityHint[] {
  const hints: DataQualityHint[] = [];

  // No power assessments → the heatmap and Powers Score are zero.
  if (view.powers && view.powers.totalPercent === 0 && view.powers.teamMembers.length > 0) {
    hints.push({
      key: "no-powers",
      label: {
        id: "match.result.hintNoPowersLabel",
        defaultMessage: "No power self-assessments",
      },
      body: {
        id: "match.result.hintNoPowersBody",
        defaultMessage:
          "The selected people have not self-assessed their powers. All powers panels therefore stay at zero. As soon as they maintain powers in their profile, the result fills in automatically.",
      },
    });
  }

  // No conditions defined → Coverage / the requirements matrix look empty by design.
  if (view.factSkill && view.factSkill.criteria.length === 0) {
    hints.push({
      key: "no-conditions",
      label: {
        id: "match.result.hintNoConditionsLabel",
        defaultMessage: "No requirements defined",
      },
      body: {
        id: "match.result.hintNoConditionsBody",
        defaultMessage:
          "This match has no requirements in the Challenge step – so the match analysis and the requirements matrix stay empty. Define conditions to see how the team fulfils them.",
      },
    });
  }

  // No demographic data → demographics chips hidden + Ø Alter shows „–".
  if (
    view.teamMatch &&
    view.teamMatch.minAge === 0 &&
    view.teamMatch.maxAge === 0 &&
    view.teamMatch.maleCount === 0 &&
    view.teamMatch.femaleCount === 0
  ) {
    hints.push({
      key: "no-demographics",
      label: {
        id: "match.result.hintNoDemographicsLabel",
        defaultMessage: "No demographic data",
      },
      body: {
        id: "match.result.hintNoDemographicsBody",
        defaultMessage:
          "The people have no birth dates and no gender on file. Department, gender and age below the team therefore stay hidden.",
      },
    });
  }

  return hints;
}

export function DataQualityHintsPanel({ view }: { view: TeamResultView }) {
  const intl = useIntl();
  const t = tx(intl);
  const hints = buildDataQualityHints(view);
  if (hints.length === 0) return null;
  return (
    <div className="box-flat bg-ink/[0.06] p-5">
      <BoxHeader
        title={<FormattedMessage id="match.result.dataHint" defaultMessage="Data note" />}
        right={<span className="text-smycles-label text-ink tabular-nums">{hints.length}</span>}
      />
      <ul className="space-y-3">
        {hints.map((h) => (
          <li key={h.key} className="grid grid-cols-[auto_minmax(0,1fr)] gap-3">
            <span
              className="mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-accent/15 text-smycles-micro font-semibold text-ink-accent"
              aria-hidden
            >
              i {/* i18n-allow: info glyph */}
            </span>
            <div>
              <div className="text-smycles-meta font-medium text-ink">{t(h.label)}</div>
              <div className="text-smycles-label leading-snug text-ink-muted">{t(h.body)}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Anforderungs-Matrix (Disclosure body) ────────────────────────────────

export function FactsAndSkillsBody({
  view,
  persons,
  requirementLeniency,
}: {
  view: TeamResultView;
  persons: ResultPerson[];
  requirementLeniency?: RequirementLeniencyView | null;
}) {
  const intl = useIntl();
  const fs = view.factSkill;
  const labelLookup = useConditionLabelLookup();
  const conditionLabelFormatters = useConditionLabelFormatters(intl);
  if (!fs) return <EmptyHint />;
  const fulfillment = buildFactSkillFulfillment(fs, view.teamMatch);
  const leniencyDependent = leniencyDependentCriteria(fs, requirementLeniency);
  const total = Math.round(fulfillment.totalPercent * 100);
  const basicsGroup = fulfillment.groups.BASICS;
  const expertiseGroup = fulfillment.groups.EXPERTISE;
  // Preserve the original criterion index — it's what we use to pair with
  // teamMember.items[] positionally (genesis doesn't always send condition.name,
  // so name-based lookup collapses columns when names are missing).
  const indexedCriteria = fs.criteria.map((c, i) => ({ ...c, originalIndex: i }));
  const basicsCriteria = indexedCriteria.filter((c) => c.group === "BASICS");
  const expertiseCriteria = indexedCriteria.filter((c) => c.group === "EXPERTISE");
  const columnCriteria = [...basicsCriteria, ...expertiseCriteria];
  const personById = new Map(persons.map((p) => [p.id, p]));
  // member.totalPercent von genesis mit #160 entfernt -> aus den Atomen abgeleitet (@adr SPARK-0030-match-result-scoring-atom-derived@1 §2).
  const rows = [...fs.teamMembers].sort((a, b) => memberFactSkillScore(b) - memberFactSkillScore(a));
  const matchedBadgeLabel = (met: number) =>
    intl.formatMessage(
      {
        id: "match.result.facts.matchedBadge",
        defaultMessage: "Trägt zu {met} von {total} Anforderungen bei",
      },
      { met, total: fs.criteria.length },
    );

  return (
    <div className="space-y-5">
      {fulfillment.violatedMustCount > 0 ? (
        // Muss-Verstoesse unabhaengig vom Gesamt-Score sichtbar machen (spark #151, @adr SPARK-0030-match-result-scoring-atom-derived@1 §1:
        // Assess meldet, verwirft nicht). Optimize liefert bei verletztem Muss stattdessen
        // Infeasibility (#150), daher greift dieses Banner primaer im Bewerten-Modus.
        <div className="flex items-center gap-2 rounded-md bg-rose-500/10 px-3 py-2 text-smycles-meta text-rose-700">
          <span className="shrink-0" aria-hidden="true"><LockClosedIcon /></span>
          <FormattedMessage
            id="match.result.facts.mustViolated"
            defaultMessage="{count, plural, one {# Muss-Kriterium ist nicht erfüllt} other {# Muss-Kriterien sind nicht erfüllt}}."
            values={{ count: fulfillment.violatedMustCount }}
          />
        </div>
      ) : null}
      {leniencyDependent.length > 0 ? (
        // Nicht jeder Kulanz-Treffer ist eine Nachricht - aber ein Kriterium, das OHNE Kulanz
        // kippt, ist eine: es haelt nur, weil die geforderte Stufe nicht belegt sein muss.
        <div className="rounded-md bg-ink/[0.04] px-3 py-2 text-smycles-meta leading-snug text-ink">
          <FormattedMessage
            id="match.result.facts.leniencyDependent"
            defaultMessage="{count, plural, one {# Anforderung gilt nur kulant als erfüllt} other {# Anforderungen gelten nur kulant als erfüllt}}: Fachwissen vorhanden, geforderte Stufe nicht belegt."
            values={{ count: leniencyDependent.length }}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-6">
        <Donut percent={total} label={intl.formatMessage({ id: "match.result.groupTotal", defaultMessage: "Total" })} />
        {basicsGroup && (
          <Stat
            label={intl.formatMessage({ id: "match.result.groupBasics", defaultMessage: "Basics" })}
            value={`${basicsGroup.fulfilled}/${basicsGroup.total}`}
            sub={`${Math.round(basicsGroup.percent * 100)} %`}
          />
        )}
        {expertiseGroup && (
          <Stat
            label={intl.formatMessage({ id: "match.result.groupExpertise", defaultMessage: "Expertise" })}
            value={`${expertiseGroup.fulfilled}/${expertiseGroup.total}`}
            sub={`${Math.round(expertiseGroup.percent * 100)} %`}
          />
        )}
      </div>

      {basicsCriteria.length + expertiseCriteria.length === 0 ? (
        <div className="text-smycles-label text-ink-muted">
          <FormattedMessage
            id="match.result.noRequirementsEmpty"
            defaultMessage="This match definition has no requirements – the requirements matrix stays empty."
          />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-smycles-label">
            <thead>
              <tr className="text-ink-muted">
                <th className="sticky left-0 bg-surface px-3 py-2 text-left font-normal">&nbsp;</th>
                {basicsCriteria.length > 0 && (
                  <th
                    colSpan={basicsCriteria.length}
                    className="border-b border-ink/10 px-2 py-2 text-center font-medium"
                  >
                    <FormattedMessage id="match.result.groupBasics" defaultMessage="Basics" />
                  </th>
                )}
                {expertiseCriteria.length > 0 && (
                  <th
                    colSpan={expertiseCriteria.length}
                    className="border-b border-ink/10 px-2 py-2 text-center font-medium"
                  >
                    <FormattedMessage id="match.result.groupExpertise" defaultMessage="Expertise" />
                  </th>
                )}
              </tr>
              <tr className="text-ink-muted align-bottom">
                <th className="sticky left-0 bg-surface px-3 py-2 text-left font-normal">&nbsp;</th>
                {columnCriteria.map((c) => {
                  const predicate = labelForCondition(c.condition, labelLookup, conditionLabelFormatters);
                  const constraint = constraintSummary(c.condition);
                  const isFulfilled = fulfillment.byCriterionIndex[c.originalIndex] ?? false;
                  const isMust = fulfillment.mustByCriterionIndex[c.originalIndex] ?? false;
                  const mustLabel = intl.formatMessage({ id: "match.result.ranking.must", defaultMessage: "Muss" });
                  return (
                    <th
                      key={c.originalIndex}
                      className="px-3 py-2 text-center align-bottom font-normal"
                      title={`${constraint ? `${predicate} (${constraint})` : predicate}${isMust ? ` · ${mustLabel}` : ""}`}
                    >
                      <div className="inline-flex max-w-[8rem] items-start gap-1 text-smycles-label leading-tight">
                        <span
                          className={`mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${isFulfilled ? "bg-emerald-500" : "bg-rose-500"}`}
                          aria-hidden="true"
                        />
                        <span className="break-words text-left">
                          {isMust ? (
                            <span
                              className={`mr-0.5 inline-flex align-[-2px] ${isFulfilled ? "text-ink-muted" : "text-rose-600"}`}
                              title={mustLabel}
                              aria-label={mustLabel}
                            >
                              <LockClosedIcon />
                            </span>
                          ) : null}
                          {predicate}
                          {constraint ? ` (${constraint})` : ""}
                        </span>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const p = personById.get(row.personId);
                return (
                  <tr key={row.personId} className="border-t border-ink/5">
                    <td className="sticky left-0 bg-surface px-3 py-2 text-smycles-label">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/p/${row.personId}`}
                          className="truncate font-medium outline-none transition hover:text-ink-accent focus-visible:ring-2 focus-visible:ring-ink-accent-ui rounded"
                          title={intl.formatMessage(
                            { id: "match.result.openProfileTitle", defaultMessage: "{name} – open profile" },
                            { name: p?.name ?? row.personId },
                          )}
                        >
                          {p?.name ?? row.personId}
                        </Link>
                        <Tooltip content={matchedBadgeLabel(row.items.filter((i) => i.matched).length)}>
                          <span className="cursor-help rounded bg-accent/10 px-1.5 py-0.5 text-smycles-micro tabular-nums text-ink-accent">
                            {row.items.filter((i) => i.matched).length}
                          </span>
                        </Tooltip>
                      </div>
                    </td>
                    {columnCriteria.map((c) => {
                      const it = row.items[c.originalIndex];
                      const glyph = it?.matched ? (
                        <span className="text-ink-accent">✓</span>
                      ) : (
                        <span className="text-ink-muted">○</span>
                      );
                      // Die KI-Begründung je Kriterium und Person (genesis: aiMode != NO_AI). Sie
                      // steht in jedem Modus im Blob und gehört genau hierhin: das Häkchen allein
                      // sagt nicht, ob es im Profil STAND oder ob das Modell es erkannt hat. Auch
                      // bei ○ vorhanden – dann ist es die Begründung der Ablehnung.
                      const aiReason = it?.aiReason?.trim() || null;
                      if (!aiReason) {
                        return (
                          <td key={c.originalIndex} className="px-2 py-2 text-center">
                            {glyph}
                          </td>
                        );
                      }
                      // Kulanz-Treffer sind eine Teilmenge der KI-Treffer (sie brauchen die
                      // aiReason), deshalb verfeinert "Kulant" die KI-Marke statt sie zu ergaenzen.
                      const lenient = isLenientLevelMatch(
                        c.condition,
                        fulfillment.mustByCriterionIndex[c.originalIndex] ? "MUST" : "OPTIONAL",
                        aiReason,
                        it?.matched === true,
                        requirementLeniency,
                      );
                      const lenientHint = intl.formatMessage({
                        id: "match.result.ranking.lenientMatchHint",
                        defaultMessage:
                          "Erfüllt per kulanter Anforderungsprüfung: Fachwissen vorhanden, geforderte Stufe nicht belegt.",
                      });
                      return (
                        <td key={c.originalIndex} className="px-2 py-2 text-center">
                          <Tooltip
                            content={lenient ? `${lenientHint} — ${aiReason}` : aiReason}
                            interactive
                            contentClassName="max-w-[22rem]"
                          >
                            <span className="cursor-help">
                              {glyph}
                              <sup className="ml-0.5 text-smycles-micro tracking-wide text-ink-accent">
                                {lenient ? (
                                  <FormattedMessage
                                    id="match.result.facts.lenientMark"
                                    defaultMessage="Kulant"
                                  />
                                ) : (
                                  <FormattedMessage id="match.result.facts.aiMark" defaultMessage="KI" />
                                )}
                              </sup>
                            </span>
                          </Tooltip>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Anforderungs-Matrix mit Klassisch/Modern-Toggle im Disclosure-Header: Klassisch zeigt die
// bestehende Tabelle (FactsAndSkillsBody), Modern das Koordinatensystem-Diagramm
// (RequirementMatrixDiagram) - dieselben echten RequirementAnalysis-Daten, keine zwei
// Datenquellen. Modus liegt in ?variant= (Default "modern", spark #250 Review), damit ein
// Reload/Teilen-Link die Wahl behaelt.
export function FactsAndSkillsBodyWithViewToggle({
  view,
  persons,
  requirementLeniency,
}: {
  view: TeamResultView;
  persons: ResultPerson[];
  requirementLeniency?: RequirementLeniencyView | null;
}) {
  const intl = useIntl();
  const labelLookup = useConditionLabelLookup();
  const conditionLabelFormatters = useConditionLabelFormatters(intl);
  const [searchParams, setSearchParams] = useSearchParams();
  const title = <FormattedMessage id="match.result.factsAndSkills" defaultMessage="Anforderungs-Matrix" />;

  type DisplayMode = "classic" | "modern";
  const mode: DisplayMode = searchParams.get("variant") === "classic" ? "classic" : "modern";

  // Klassisch (lange Tabelle) und Modern (festes 280px-Diagramm) haben grundverschiedene Hoehen,
  // und React ersetzt beim Wechsel den kompletten Body durch neue DOM-Knoten (kein gemeinsames
  // Element bleibt bestehen). Der Browser springt dabei manchmal im Scroll (Scroll-Anchoring
  // greift offenbar ins Leere, wenn sein gewaehlter Anker mit ausgetauscht wird) - deshalb messen
  // wir die Position des Toggle-Kopfs selbst VOR dem Wechsel und korrigieren NACH dem Reflow
  // aktiv zurueck, statt uns auf Browser-Heuristik zu verlassen.
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollAnchorRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (scrollAnchorRef.current == null || !containerRef.current) return;
    const newTop = containerRef.current.getBoundingClientRect().top;
    window.scrollBy(0, newTop - scrollAnchorRef.current);
    scrollAnchorRef.current = null;
  }, [mode]);

  const setMode = (next: DisplayMode) => {
    if (containerRef.current) {
      scrollAnchorRef.current = containerRef.current.getBoundingClientRect().top;
    }
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("variant", next);
        return params;
      },
      { replace: true },
    );
  };

  const toggle = (
    <SegmentedControl<DisplayMode>
      size="sm"
      value={mode}
      onChange={setMode}
      options={[
        { value: "classic", label: intl.formatMessage({ id: "profile.skills.viewList", defaultMessage: "Klassisch" }) },
        { value: "modern", label: intl.formatMessage({ id: "profile.skills.viewDiagram", defaultMessage: "Modern" }) },
      ]}
    />
  );

  if (mode === "classic") {
    return (
      <div ref={containerRef}>
        {/* dividerPlacement="bottom" in beiden Modi (s.u.), damit die Trennlinie beim Umschalten
            Klassisch/Modern nicht zwischen oben und unten hin- und herspringt. */}
        <Disclosure title={title} headerAction={toggle} dividerPlacement="bottom">
          <FactsAndSkillsBody view={view} persons={persons} requirementLeniency={requirementLeniency} />
        </Disclosure>
      </div>
    );
  }

  // Numerische Stufe ("3") statt Stufenname ("Contributor") - das Diagramm braucht die Zahl fuer
  // die X-Achse/knappe Chips, der Produktionscode (skillLevelName) bleibt unangetastet, das hier
  // ist nur ein lokaler Formatter-Override fuer diese Ansicht.
  const numericLevelFormatters: ConditionLabelFormatters = {
    ...conditionLabelFormatters,
    skillLevelName: (level) => String(level.value),
  };
  const label = (a: RequirementAnalysis) =>
    labelForCondition(a.condition, labelLookup, numericLevelFormatters);
  const analysis = buildRequirementAnalysis(view.factSkill);
  // Dieselbe Ableitung wie das "gilt nur kulant als erfuellt"-Banner in FactsAndSkillsBody: ein
  // Kriterium ist NUR wegen der Kulanz-Treffer team-weit erfuellt (ohne sie wuerde die Composition
  // kippen). Modern zeigt das als eigene Ampelfarbe statt es wie in der Tabelle zu verstecken.
  const lenientIndexes = new Set(leniencyDependentCriteria(view.factSkill, requirementLeniency));
  const diagramProps: RequirementMatrixDiagramProps = { analysis, persons, label, lenientIndexes };

  return (
    <div ref={containerRef}>
      <Disclosure title={title} headerAction={toggle} dividerPlacement="bottom">
        <RequirementMatrixDiagram {...diagramProps} />
      </Disclosure>
    </div>
  );
}


function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="box bg-surface px-4 py-3">
      <div className="smycles-field-label">{label}</div>
      <div className="mt-1 font-serif text-smycles-title tabular-nums">{value}</div>
      {sub && <div className="text-smycles-micro text-ink-muted">{sub}</div>}
    </div>
  );
}

// Score-Kachel der Powers-Matrix. Selbstbewertungen liegen praktisch immer zwischen 50
// und 100, deshalb spannt die Fuellung genau diesen Bereich: alles <= 50 ist maximal
// hell, 100 ist volles Ink. Eine 0-100-Skala macht alle Werte gleich dunkel (das war der
// Zustand mit scoreBucket).
// ponytail: fester Nullpunkt 50 - falls echte Werte darunter auftauchen, hier auf ein
// datenabhaengiges Minimum umstellen.
const CHIP_FLOOR = 50;

// The fill is an accent mix painted inline, so no surface role describes it:
// the label is picked against the mixed colour itself, the way
// skill-level-style.ts does for its swatches. The chip sits on the card.
function chipFill(value: number | null, accent: string, surface: string): { style: CSSProperties; text: string } {
  if (value === null) return { style: { backgroundColor: "color-mix(in srgb, var(--context-ink) 4%, transparent)" }, text: "text-ink-muted" };
  const t = Math.min(1, Math.max(0, (value - CHIP_FLOOR) / (100 - CHIP_FLOOR)));
  const percent = 8 + t * 92;
  return {
    style: {
      backgroundColor: `color-mix(in srgb, var(--color-accent) ${percent.toFixed(1)}%, transparent)`,
      color: pickInk(mixHex(surface, accent, percent / 100)),
    },
    text: "",
  };
}

function ScoreChip({ value, strong = false }: { value: number | null; strong?: boolean }) {
  const accent = useThemePrefs((s) => s.accentColor);
  const surface = useThemePrefs((s) => s.surfaceColor);
  const fill = chipFill(value, accent, surface);
  return (
    <div
      className={`mx-auto grid h-6 w-[85%] min-w-0 place-items-center rounded text-smycles-micro tabular-nums ${fill.text} ${
        strong ? "font-medium" : ""
      }`}
      style={fill.style}
    >
      {value === null ? "–" : Math.round(value)}
    </div>
  );
}

// ─── Team Powers (Disclosure body) ────────────────────────────────────────

export function TeamPowersBody({
  view,
  persons,
  openColumns,
  onToggleColumn,
}: {
  view: TeamResultView;
  persons: ResultPerson[];
  // Zustand lebt in ResultStep.tsx (PostSolveDashboard), damit der externe Pill-Switch
  // ("Cluster"/"Alle 30 Powers") seinen Zustand aus der tatsächlichen Spaltenlage ableiten
  // kann - klappt man hier manuell alle Spalten auf, zeigt der Pill von selbst "Alle 30
  // Powers", statt eine unabhängige, potenziell falsche Meinung zu haben.
  openColumns: Set<string>;
  onToggleColumn: (key: string) => void;
}) {
  const intl = useIntl();
  const personById = useMemo(() => new Map(persons.map((p) => [p.id, p])), [persons]);
  const rows = buildHeatmapRows(view.powers, persons);
  const groups = useMemo(() => buildPowerColumnGroups(view.powers), [view.powers]);
  // ponytail: PLATZHALTER, kein toter Code. Diese Tabelle ("Element Requirements") rendert
  // heute NIE: sie liest factSkill.criteria mit group === "POWERS", und genesis vergibt diese
  // Gruppe nirgends - buildCriterion (MatchResultGeneratePostCalculation) mappt keine
  // MatchCondition darauf, weil es noch keine Power-Anforderungen gibt (nur Gewichte in der
  // Powers Scorecard, und ein Gewicht ist keine Auflage). Sobald genesis eine Power-Condition
  // bekommt, geht der Block von selbst an. Nicht loeschen, nicht debuggen.
  const reqs = buildElementRequirementRows(view.factSkill, persons);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const isColumnOpen = (key: string) => openColumns.has(key);
  const toggleColumn = onToggleColumn;
  // Alle Score-Spalten gleich breit: table-fixed + colgroup, sonst diktiert die laengste
  // Cluster-Ueberschrift die Spaltenbreite und die Kacheln stehen versetzt.
  const NAME_COL_PERCENT = 14;
  const blockPercent = groups.length > 0 ? (100 - NAME_COL_PERCENT) / groups.length : 0;
  const colWidths = groups.flatMap((g) =>
    isColumnOpen(g.key) ? g.elements.map(() => blockPercent / g.elements.length) : [blockPercent],
  );

  if (!view.powers && reqs.columns.length === 0) {
    return <EmptyHint />;
  }

  return (
    <div className="space-y-6">
      {view.powers && groups.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full table-fixed border-collapse text-smycles-label">
            <colgroup>
              <col style={{ width: `${NAME_COL_PERCENT}%` }} />
              {colWidths.map((w, i) => (
                <col key={i} style={{ width: `${w.toFixed(4)}%` }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th rowSpan={2} className="sticky left-0 z-10 bg-surface px-3 py-2 text-left font-normal text-ink-muted">
                  &nbsp;
                </th>
                {groups.map((g) => {
                  const colOpen = isColumnOpen(g.key);
                  const clusterLabel = intl.formatMessage({ id: powerGroupLabelId(g.key) });
                  const ariaLabel = intl.formatMessage(
                    {
                      id: colOpen ? "match.result.powers.collapseClusterAria" : "match.result.powers.expandClusterAria",
                      defaultMessage: colOpen ? "{cluster} einklappen" : "{cluster} aufklappen \u2013 einzelne Powers anzeigen",
                    },
                    { cluster: clusterLabel },
                  );
                  return (
                    // Keine Hover-Markierung auf der Gruppennamen-Zeile selbst - die spaltenweite
                    // Markierung beginnt erst unterhalb der Kürzel-Zeile, bei den Werten. Padding
                    // sitzt auf dem Button statt auf dem th, damit die ganze Kopfzelle klickbar
                    // ist, nicht nur der enge Textbereich.
                    <th
                      key={g.key}
                      colSpan={colOpen ? g.elements.length : 1}
                      className="border-l border-ink/5 p-0 align-bottom text-center text-smycles-label font-medium leading-tight text-ink"
                    >
                      <button
                        type="button"
                        onClick={() => toggleColumn(g.key)}
                        aria-expanded={colOpen}
                        aria-label={ariaLabel}
                        className="block h-full w-full cursor-pointer rounded px-1.5 pt-2 pb-1 outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
                      >
                        {clusterLabel}
                      </button>
                    </th>
                  );
                })}
              </tr>
              <tr>
                {groups.map((g) => {
                  const colOpen = isColumnOpen(g.key);
                  const clusterLabel = intl.formatMessage({ id: powerGroupLabelId(g.key) });
                  const ariaLabel = intl.formatMessage(
                    {
                      id: colOpen ? "match.result.powers.collapseClusterAria" : "match.result.powers.expandClusterAria",
                      defaultMessage: colOpen ? "{cluster} einklappen" : "{cluster} aufklappen \u2013 einzelne Powers anzeigen",
                    },
                    { cluster: clusterLabel },
                  );
                  // Kein hoveredKey-Highlight hier - die Kürzel-Zeile bleibt Teil des klickbaren
                  // Spaltenkopfs, aber die farbliche Markierung beginnt erst darunter im
                  // Tabellenkörper, bei den Werten.
                  if (!colOpen) {
                    return <th key={g.key} className="border-l border-ink/5" />;
                  }
                  return g.elements.map((el, idx) => (
                    <th key={el} className={`p-0 text-center ${idx === 0 ? "border-l border-ink/5" : ""}`}>
                      <button
                        type="button"
                        onClick={() => toggleColumn(g.key)}
                        aria-label={ariaLabel}
                        title={intl.formatMessage({ id: elementNameId(el), defaultMessage: elementDisplayName(el) })}
                        className="block h-full w-full cursor-pointer rounded px-1.5 pb-1 text-smycles-micro font-normal text-ink-muted outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
                      >
                        {ELEMENT_CODES[el] ?? el.slice(0, 3)}
                      </button>
                    </th>
                  ));
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const unmeasured = row.totalPercent == null;
                // Unbewertete stehen am Ende (buildHeatmapRows sortiert sie dorthin), aber sie
                // sind kein Rangplatz - die staerkere Linie trennt sie als eigenen Block ab,
                // sonst liest sich die letzte Zeile wie die schwaechste Person im Team.
                const blockStart = unmeasured && rows[rowIndex - 1]?.totalPercent != null;
                return (
                <tr
                  key={row.personId}
                  className={`border-t ${blockStart ? "border-ink/15" : "border-ink/5"}`}
                  data-testid={unmeasured ? `powers-row-unmeasured-${row.personId}` : undefined}
                >
                  <td className="sticky left-0 z-10 bg-surface px-3 py-1.5">
                    <div className="flex min-w-0 items-center gap-2">
                      {/* Personen-Avatar statt Power-Icons in der Kopfzeile - die Icons sollten
                          zeigen "wer", nicht "welche Power". */}
                      {(() => {
                        const person = personById.get(row.personId);
                        return person ? <Avatar person={person} size="sm" /> : null;
                      })()}
                      <Link
                        to={`/p/${row.personId}`}
                        className="truncate font-medium outline-none transition hover:text-ink-accent focus-visible:ring-2 focus-visible:ring-ink-accent-ui rounded"
                        title={intl.formatMessage(
                          { id: "match.result.openProfileTitle", defaultMessage: "{name} \u2013 open profile" },
                          { name: row.personName },
                        )}
                      >
                        {row.personName}
                      </Link>
                      <Tooltip
                        content={intl.formatMessage(
                          row.totalPercent == null
                            ? {
                                id: "match.result.powers.memberBadgeUnmeasured",
                                defaultMessage: "Ohne Powers-Daten – zählt in keinem Schnitt mit",
                              }
                            : {
                                id: "match.result.powers.memberBadge",
                                defaultMessage: "Mittel aller 30 Powers dieser Person (0\u2013100)",
                              },
                        )}
                      >
                        <span className="shrink-0 cursor-help rounded bg-accent/10 px-1.5 py-0.5 text-smycles-micro tabular-nums text-ink-accent">
                          {row.totalPercent == null
                            ? "–" /* i18n-allow: Gedankenstrich fuer "nicht eingeschaetzt" (@adr SPARK-0028-no-em-dashes-in-ui-text@1: en dash) */
                            : Math.round(row.totalPercent).toLocaleString()}
                        </span>
                      </Tooltip>
                    </div>
                  </td>
                  {groups.map((g) => {
                    // Hover + Klick gelten pro Spalte (Cluster), nicht pro Zeile -
                    // "onMouseEnter/Leave" auf jeder Zelle derselben Spalte teilt sich denselben
                    // hoveredKey, sodass optisch die ganze Spalte \u00fcber alle Zeilen hinweg markiert
                    // wird, nicht nur die eine Zelle unter dem Mauszeiger.
                    const colOpen = isColumnOpen(g.key);
                    const clusterLabel = intl.formatMessage({ id: powerGroupLabelId(g.key) });
                    const cellAriaLabel = intl.formatMessage(
                      {
                        id: colOpen ? "match.result.powers.collapseClusterAria" : "match.result.powers.expandClusterAria",
                        defaultMessage: colOpen ? "{cluster} einklappen" : "{cluster} aufklappen \u2013 einzelne Powers anzeigen",
                      },
                      { cluster: clusterLabel },
                    );
                    // Padding sitzt auf dem Button statt auf der td - so ist die ganze Zellfl\u00e4che
                    // klickbar, nicht nur die enge ScoreChip-Box in der Mitte.
                    const cellClass = `transition-colors ${hoveredKey === g.key ? "bg-accent/5" : ""}`;
                    if (!colOpen) {
                      return (
                        <td
                          key={g.key}
                          className={`border-l border-ink/5 p-0 ${cellClass}`}
                          onMouseEnter={() => setHoveredKey(g.key)}
                          onMouseLeave={() => setHoveredKey(null)}
                        >
                          <button
                            type="button"
                            onClick={() => toggleColumn(g.key)}
                            aria-label={cellAriaLabel}
                            className="toggle-pop block h-full w-full cursor-pointer rounded px-1 py-1 outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
                          >
                            <ScoreChip value={clusterMean(row.cells, g.key)} strong />
                          </button>
                        </td>
                      );
                    }
                    return g.elements.map((el, idx) => (
                      <td
                        key={el}
                        className={`p-0 ${idx === 0 ? "border-l border-ink/5" : ""} ${cellClass}`}
                        onMouseEnter={() => setHoveredKey(g.key)}
                        onMouseLeave={() => setHoveredKey(null)}
                      >
                        <button
                          type="button"
                          onClick={() => toggleColumn(g.key)}
                          aria-label={cellAriaLabel}
                          className="toggle-pop block h-full w-full cursor-pointer rounded px-1 py-1 outline-none focus-visible:ring-2 focus-visible:ring-ink-accent-ui"
                        >
                          <ScoreChip value={row.cells.find((c) => c.element === el)?.value ?? null} />
                        </button>
                      </td>
                    ));
                  })}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {reqs.columns.length > 0 && (
        <div className="border-t border-ink/10 pt-5">
          <div className="smycles-field-label mb-3">
            <FormattedMessage
              id="match.result.elementRequirementsFulfilled"
              defaultMessage="Element Requirements · {count} fulfilled"
              values={{ count: reqs.rows.reduce((a, r) => a + r.score, 0) }}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-smycles-label">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-surface px-3 py-2 text-left font-normal text-ink-muted">
                    &nbsp;
                  </th>
                  {reqs.columns.map((col, i) => (
                    <th key={i} className="px-2 py-2 text-center font-normal text-ink-muted">
                      <div className="text-smycles-micro">{col.label}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reqs.rows.map((r) => (
                  <tr key={r.personId} className="border-t border-ink/5">
                    <td className="sticky left-0 bg-surface px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/p/${r.personId}`}
                          className="font-medium outline-none transition hover:text-ink-accent focus-visible:ring-2 focus-visible:ring-ink-accent-ui rounded"
                          title={intl.formatMessage(
                            { id: "match.result.openProfileTitle", defaultMessage: "{name} – open profile" },
                            { name: r.personName },
                          )}
                        >
                          {r.personName}
                        </Link>
                        <Tooltip
                          content={intl.formatMessage(
                            {
                              id: "match.result.powers.reqBadge",
                              defaultMessage: "Trägt zu {met} von {total} Power-Anforderungen bei",
                            },
                            { met: r.score, total: reqs.columns.length },
                          )}
                        >
                          <span className="cursor-help rounded bg-accent/10 px-1.5 py-0.5 text-smycles-micro tabular-nums text-ink-accent">
                            {r.score}
                          </span>
                        </Tooltip>
                      </div>
                    </td>
                    {r.cells.map((c, i) => (
                      <td key={i} className="px-2 py-2 text-center">
                        {c.matched ? <span className="text-ink-accent">✓</span> : <span className="text-ink-muted">○</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
